import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listingUrl } from '@/lib/listing';
import { track } from '@/lib/analytics';
import {
  Car,
  UserSearch,
  Languages,
  ShieldCheck,
  Filter,
  Phone,
  Mail,
  MapPin,
  ImagePlus,
  MessageSquare,
  BadgeCheck,
  Plus,
  Menu,
  X,
  Settings,
  ChevronLeft,
  ChevronRight,
  Award,
  Lock,
} from 'lucide-react';
import api from '@/lib/axios';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';
import NewsHomeBlock from '@/components/marketplace/NewsHomeBlock';
import { AdSlot } from '@/components/marketplace/ads/AdSlot';
import FaqSection from '@/components/marketplace/FaqSection';
import ListingCommerceBlock from '@/components/marketplace/ListingCommerceBlock';
import PublicNameCard from '@/components/marketplace/PublicNameCard';
import PromoBadges from '@/components/marketplace/PromoBadges';
import { PremiumHero, PlatformBadges, PortugalNeonMap, ConfidenceCard, InstitutionalStrip, HeroComposition } from '@/components/marketplace/HeroNeonDecor';
import { useSeo } from '../hooks/useSeo';
import { useCmsContent } from '../hooks/useCmsContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Resolve URL de imagem: base64 fica intacto, paths absolutos do backend (/api/...) ganham prefixo. */
const resolveImageUrl = (src) => {
  if (!src) return '';
  if (src.startsWith('data:') || src.startsWith('http://') || src.startsWith('https://')) return src;
  if (src.startsWith('/api/')) return `${BACKEND_URL}${src}`;
  return src;
};

const uiCopy = {
  pt: {
    title: 'Marketplace TVDE',
    subtitle: 'Viaturas disponíveis para aluguer TVDE',
    subtitleEn: 'TVDE Cars Available for Rental',
    freeLaunch: 'Lançamento Marketplace Gratuito · 30 dias para viaturas e motoristas',
    tabCars: 'Viaturas',
    tabDrivers: 'Motoristas',
    openCarForm: 'Anunciar a minha Viatura',
    openDriverForm: 'Anunciar Procura de Viatura',
    city: 'Cidade',
    fuel: 'Combustível',
    maxPrice: 'Preço máx/semana',
    apply: 'Aplicar filtros',
    carsList: 'Viaturas disponíveis',
    driversList: 'Motoristas disponíveis',
    salesList: 'Viaturas à venda',
    loading: 'A carregar listagens...',
    empty: 'Sem resultados para estes filtros. Tente limpar os filtros.',
    loadError: 'Não foi possível carregar as publicações. Pode ser um problema temporário de ligação.',
    retry: 'Tentar novamente',
    sendCode: 'Enviar código',
    verifyCode: 'Verificar código',
    submitCar: 'Submeter viatura para validação',
    submitDriver: 'Submeter perfil de motorista para validação',
    contact: 'Contactar',
    leadTitle: 'Enviar contacto',
    leadSubmit: 'Enviar mensagem',
    enterButton: 'Entrar',
    publishButton: 'Publicar',
    publishVehicleOption: 'Anunciar a minha Viatura',
    publishDriverOption: 'Anunciar Procura de Viatura',
    backSite: 'Voltar ao site',
    verifiedAdvertiser: 'Contacto confirmado',
    verifiedDriver: 'Contacto confirmado',
  },
  en: {
    title: 'TVDE Marketplace',
    subtitle: 'TVDE Cars Available for Rental',
    subtitleEn: 'Viaturas disponíveis para aluguer TVDE',
    freeLaunch: 'Free marketplace launch · 30 days for cars and drivers',
    tabCars: 'Cars',
    tabDrivers: 'Drivers',
    openCarForm: 'Publish Car',
    openDriverForm: 'Share Search',
    city: 'City',
    fuel: 'Fuel',
    maxPrice: 'Max price / week',
    apply: 'Apply filters',
    carsList: 'Available cars',
    driversList: 'Available drivers',
    salesList: 'Cars for sale',
    loading: 'Loading listings...',
    empty: 'No results for these filters yet.',
    loadError: 'We could not load the listings. This may be a temporary connection issue.',
    retry: 'Try again',
    sendCode: 'Send code',
    verifyCode: 'Verify code',
    submitCar: 'Submit car for moderation',
    submitDriver: 'Submit driver profile for moderation',
    contact: 'Contact',
    leadTitle: 'Send contact',
    leadSubmit: 'Send message',
    enterButton: 'Enter',
    publishButton: 'Publish',
    publishVehicleOption: 'Publish Car',
    publishDriverOption: 'Advertise Vehicle Search',
    backSite: 'Back to site',
    verifiedAdvertiser: 'Contact confirmed',
    verifiedDriver: 'Contact confirmed',
  },
};

const baseFilters = { cidade: '', combustivel: '', precoMax: '' };

const initialVehicleForm = {
  primeiro_nome: '',
  ultimo_nome: '',
  email: '',
  telefone: '',
  mostrar_telefone: false,
  mostrar_email: false,
  verification_token: '',
  phone_verification_token: '',
  phone_code: '',
  code: '',
  titulo: '',
  descricao: '',
  cidade: '',
  marca: '',
  modelo: '',
  combustivel: 'Híbrido',
  caixa: 'Automática',
  valor_semanal: '',
  ano: '',
  fotos: [],
  duracao_dias: 30,
  finalidade: 'aluguer',
  preco_venda: '',
  quilometros: '',
};

const initialDriverForm = {
  primeiro_nome: '',
  ultimo_nome: '',
  email: '',
  telefone: '',
  mostrar_telefone: false,
  mostrar_email: false,
  verification_token: '',
  phone_verification_token: '',
  phone_code: '',
  code: '',
  cidade: '',
  interesse: 'aluguer',
  tipo_viatura: 'Citadino',
  combustivel: 'Elétrico',
  valor_aluguer_maximo: '',
  disponibilidade: '',
  tipo_turno: 'Tempo inteiro',
  pretende_dividir_carro: false,
  periodo_divisao: '',
  mensagem: '',
  duracao_dias: 30,
};

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * Comprime uma imagem do browser para JPEG ~70% qualidade,
 * largura máxima 1280px. Devolve um Blob pronto para upload.
 */
const compressImage = (file, maxWidth = 1280, quality = 0.72) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const ratio = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Falha ao comprimir imagem'));
            resolve(blob);
          },
          'image/jpeg',
          quality
        );
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

/** Faz upload de um File ao endpoint público e devolve a URL. */
const uploadImage = async (file) => {
  const blob = await compressImage(file);
  const fd = new FormData();
  // Nome de ficheiro determinístico (extensão jpg porque comprimimos sempre para JPEG)
  fd.append('file', new File([blob], 'foto.jpg', { type: 'image/jpeg' }));
  const res = await api.post('/marketplace/uploads', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.url; // ex: /api/marketplace/images/...
};

export default function MarketplacePage({ initialAction = null }) {
  const navigate = useNavigate();
  const routeMode = !!initialAction;
  const seoByAction = {
    viatura: {
      title: 'Anunciar Viatura TVDE | Marketplace TVDE',
      description: 'Publique a sua viatura TVDE no Marketplace TVDE de Portugal e receba contactos de motoristas Uber e Bolt.',
      path: '/anunciar-viatura',
    },
    motorista: {
      title: 'Procuro Viatura TVDE | Marketplace TVDE',
      description: 'É motorista e procura viatura TVDE? Publique o seu perfil no Marketplace TVDE e seja encontrado por quem tem viaturas disponíveis.',
      path: '/publicar-procura',
    },
    manage: {
      title: 'Gerir o meu anúncio | Marketplace TVDE',
      description: 'Aceda, edite ou remova os seus anúncios publicados no Marketplace TVDE.',
      path: '/gerir-anuncio',
    },
  };
  useSeo({
    ...(seoByAction[initialAction] || {
      title: 'Marketplace TVDE | Viaturas e Motoristas em Portugal',
      description: 'O Marketplace TVDE de Portugal. Anuncie viaturas, encontre motoristas e ligue-se ao mercado TVDE (Uber e Bolt) de forma simples e segura.',
      path: '/',
    }),
    noindex: initialAction === 'manage',
  });
  useEffect(() => { track('view_home'); }, []);
  useEffect(() => {
    api.get('/marketplace/venda/publicacoes', { params: { limit: 4, ordenar: 'recentes' } })
      .then((r) => setSaleHighlights(r.data.items || []))
      .catch(() => setSaleHighlights([]));
  }, []);

  const [lang, setLang] = useState('pt');
  const [checkoutSummary, setCheckoutSummary] = useState(null);
  const [checkoutConsent, setCheckoutConsent] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [publishMenuOpen, setPublishMenuOpen] = useState(false);
  const [tab, setTab] = useState('viatura');
  const [filters, setFilters] = useState(baseFilters);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [listError, setListError] = useState(false);
  const [openForm, setOpenForm] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPublishMenu, setShowPublishMenu] = useState(false);

  const [carForm, setCarForm] = useState(initialVehicleForm);
  const [driverForm, setDriverForm] = useState(initialDriverForm);

  const [selectedListing, setSelectedListing] = useState(null);
  const [leadForm, setLeadForm] = useState({ nome: '', email: '', telefone: '', mensagem: '', code: '', phone_code: '', verification_token: '', phone_verification_token: '', aceita_privacidade: false, aceita_alertas: false });
  const [phoneCodeState, setPhoneCodeState] = useState({ sending: false, verifying: false, sent: false, verified: false, msg: '', err: '' });
  const [leadEmailState, setLeadEmailState] = useState({ sending: false, verifying: false, sent: false, verified: false, msg: '', err: '' });
  const [leadPhoneState, setLeadPhoneState] = useState({ sending: false, verifying: false, sent: false, verified: false, msg: '', err: '' });
  const [leadState, setLeadState] = useState({ loading: false, msg: '', err: '' });
  const [mpSettings, setMpSettings] = useState(null);
  const { cms } = useCmsContent();
  const [codeState, setCodeState] = useState({ sending: false, verifying: false, sent: false, verified: false, msg: '', err: '' });

  const [publishSuccess, setPublishSuccess] = useState(null); // { expires_at, duracao_dias }

  // Lightbox público — clicar na foto de um anúncio abre todas as fotos em ecrã grande
  const [lightbox, setLightbox] = useState({ open: false, fotos: [], idx: 0, title: '' });

  // Modal de confirmação pré-submissão (quando contacto fica visível)
  const [confirmSubmit, setConfirmSubmit] = useState(null); // { type: 'viatura'|'motorista' } ou null

  // Modal "Gerir os meus anúncios" — fluxo: email -> código -> lista -> apagar
  const [manageState, setManageState] = useState({
    open: false,
    step: 'email', // 'email' | 'code' | 'list'
    email: '',
    code: '',
    token: '',
    items: [],
    loading: false,
    err: '',
    msg: '',
  });

  // Edição self-service do anúncio (draft do formulário de edição)
  const [editDraft, setEditDraft] = useState(null);

  // Fase 3 — informação comercial por anúncio (estado, validade, extras, ofertas)
  const [commerce, setCommerce] = useState({});
  const [saleHighlights, setSaleHighlights] = useState([]);
  const highlightedListings = useMemo(
    () => listings.filter((l) => (l.promocoes || []).includes('destaque_homepage')),
    [listings],
  );

  // Fase 11 — abrir o formulário correspondente quando acedido por URL próprio.
  // O React reutiliza a instância entre rotas, por isso limpamos sempre o estado anterior.
  useEffect(() => {
    if (initialAction === 'viatura' || initialAction === 'motorista') {
      setOpenForm(initialAction);
    } else {
      setOpenForm(null);
    }
    if (initialAction === 'manage') {
      setManageState({
        open: true, step: 'email', email: '', code: '', token: '',
        items: [], loading: false, err: '', msg: '',
      });
      // Link seguro enviado no aviso de validade (?t=token): pré-preenche o email.
      // O acesso continua a exigir o código de 6 dígitos.
      const token = new URLSearchParams(window.location.search).get('t');
      if (token) {
        api.get(`/marketplace/gestao/${token}`)
          .then((r) => setManageState((s) => ({
            ...s, email: r.data.email || '',
            msg: 'Email pré-preenchido a partir do link do seu aviso. Confirme com o código para continuar.',
          })))
          .catch((e) => setManageState((s) => ({
            ...s,
            err: e.response?.status === 410
              ? 'A ligação do email expirou. Introduza o seu email e peça um novo código.'
              : 'Ligação inválida. Introduza o seu email para continuar.',
          })));
      }
    } else {
      setManageState((s) => ({ ...s, open: false }));
    }
    window.scrollTo(0, 0);
  }, [initialAction]);

  useEffect(() => {
    api
      .get('/marketplace/settings')
      .then((res) => setMpSettings(res.data))
      .catch(() => setMpSettings(null));
  }, []);

  const t = useMemo(() => uiCopy[lang], [lang]);

  const loadListings = async (targetTab = tab, targetFilters = filters) => {
    setLoading(true);
    setListError(false);
    try {
      const params = { tipo: targetTab, page: 1, limit: 40 };
      if (targetFilters.cidade) params.cidade = targetFilters.cidade;
      if (targetTab === 'viatura' && targetFilters.combustivel) params.combustivel = targetFilters.combustivel;
      if (targetTab === 'viatura' && targetFilters.precoMax) params.preco_max = Number(targetFilters.precoMax);
      const response = await api.get('/marketplace/publicacoes', { params });
      setListings(response.data.items || []);
      setListError(false);
    } catch (err) {
      setListError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!routeMode) setOpenForm(null);
    setShowPublishMenu(false);
    setFilters(baseFilters);
    loadListings(tab, baseFilters);
  }, [tab]);

  const sendEmailCode = async (email) => {
    if (!email) {
      setCodeState((s) => ({ ...s, err: 'Preencha o email antes de pedir código.', msg: '' }));
      return;
    }
    setCodeState({ sending: true, verifying: false, sent: false, verified: false, msg: '', err: '' });
    try {
      await api.post('/marketplace/verificacao/enviar-codigo', { email });
      setCodeState({ sending: false, verifying: false, sent: true, verified: false, msg: 'Código enviado por email. Verifique a sua caixa de entrada (e o spam).', err: '' });
    } catch (err) {
      setCodeState({ sending: false, verifying: false, sent: false, verified: false, msg: '', err: err.response?.data?.detail || 'Falha ao enviar código.' });
    }
  };

  const verifyCarCode = async () => {
    if (!carForm.code) {
      setCodeState((s) => ({ ...s, err: 'Introduza o código de 6 dígitos.', msg: '' }));
      return;
    }
    setCodeState((s) => ({ ...s, verifying: true, msg: '', err: '' }));
    try {
      const response = await api.post('/marketplace/verificacao/confirmar', {
        email: carForm.email,
        code: carForm.code,
      });
      setCarForm((prev) => ({ ...prev, verification_token: response.data.verification_token }));
      setCodeState((s) => ({ ...s, verifying: false, verified: true, msg: 'Email validado com sucesso. Pode continuar a publicar.', err: '' }));
    } catch (err) {
      setCodeState((s) => ({ ...s, verifying: false, verified: false, msg: '', err: err.response?.data?.detail || 'Código inválido ou expirado.' }));
    }
  };

  const verifyDriverCode = async () => {
    if (!driverForm.code) {
      setCodeState((s) => ({ ...s, err: 'Introduza o código de 6 dígitos.', msg: '' }));
      return;
    }
    setCodeState((s) => ({ ...s, verifying: true, msg: '', err: '' }));
    try {
      const response = await api.post('/marketplace/verificacao/confirmar', {
        email: driverForm.email,
        code: driverForm.code,
      });
      setDriverForm((prev) => ({ ...prev, verification_token: response.data.verification_token }));
      setCodeState((s) => ({ ...s, verifying: false, verified: true, msg: 'Email validado com sucesso. Pode continuar a publicar.', err: '' }));
    } catch (err) {
      setCodeState((s) => ({ ...s, verifying: false, verified: false, msg: '', err: err.response?.data?.detail || 'Código inválido ou expirado.' }));
    }
  };

  const sendPhoneCode = async (telefone, state, setState) => {
    if (!telefone) {
      setState((s) => ({ ...s, err: 'Preencha o telemóvel antes de pedir código.', msg: '' }));
      return;
    }
    setState({ sending: true, verifying: false, sent: false, verified: false, msg: '', err: '' });
    try {
      await api.post('/marketplace/verificacao/enviar-sms', { telefone });
      setState({ sending: false, verifying: false, sent: true, verified: false, msg: 'Código enviado por SMS.', err: '' });
    } catch (err) {
      setState({ sending: false, verifying: false, sent: false, verified: false, msg: '', err: err.response?.data?.detail || 'Falha ao enviar SMS.' });
    }
  };

  const verifyPhoneCode = async (telefone, code, setForm, field, setState) => {
    if (!code) {
      setState((s) => ({ ...s, err: 'Introduza o código recebido por SMS.', msg: '' }));
      return;
    }
    setState((s) => ({ ...s, verifying: true, msg: '', err: '' }));
    try {
      const res = await api.post('/marketplace/verificacao/confirmar-sms', { telefone, code });
      setForm((prev) => ({ ...prev, [field]: res.data.verification_token }));
      setState((s) => ({ ...s, verifying: false, verified: true, msg: 'Telemóvel validado com sucesso.', err: '' }));
    } catch (err) {
      setState((s) => ({ ...s, verifying: false, verified: false, msg: '', err: err.response?.data?.detail || 'Código de SMS inválido ou expirado.' }));
    }
  };

  const sendLeadEmailCode = async (email) => {
    if (!email) { setLeadEmailState((s) => ({ ...s, err: 'Preencha o email.', msg: '' })); return; }
    setLeadEmailState({ sending: true, verifying: false, sent: false, verified: false, msg: '', err: '' });
    try {
      await api.post('/marketplace/verificacao/enviar-codigo', { email });
      setLeadEmailState({ sending: false, verifying: false, sent: true, verified: false, msg: 'Código enviado por email.', err: '' });
    } catch (err) {
      setLeadEmailState({ sending: false, verifying: false, sent: false, verified: false, msg: '', err: err.response?.data?.detail || 'Falha ao enviar código.' });
    }
  };

  const verifyLeadEmailCode = async () => {
    setLeadEmailState((s) => ({ ...s, verifying: true, msg: '', err: '' }));
    try {
      const res = await api.post('/marketplace/verificacao/confirmar', { email: leadForm.email, code: leadForm.code });
      setLeadForm((prev) => ({ ...prev, verification_token: res.data.verification_token }));
      setLeadEmailState((s) => ({ ...s, verifying: false, verified: true, msg: 'Email validado.', err: '' }));
    } catch (err) {
      setLeadEmailState((s) => ({ ...s, verifying: false, verified: false, msg: '', err: err.response?.data?.detail || 'Código inválido.' }));
    }
  };

  const triggerCheckoutIfNeeded = async (response) => {
    if (!response?.requer_pagamento) return false;
    // Em vez de redirecionar já, mostramos um resumo com consentimento (Fase 4).
    setCheckoutSummary({
      publicacao_id: response.publicacao_id,
      pacote: response.pacote || {},
      duracao_dias: response.duracao_dias,
    });
    setCheckoutConsent(false);
    setCheckoutError('');
    return true;
  };

  const proceedCheckout = async () => {
    if (!checkoutSummary) return;
    if (!checkoutConsent) {
      setCheckoutError('Tem de confirmar a declaração para prosseguir com o pagamento.');
      return;
    }
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const res = await api.post(
        `/marketplace/publicacoes/${checkoutSummary.publicacao_id}/checkout`,
        { origin_url: window.location.origin, inicio_antes_14dias: checkoutConsent }
      );
      if (res.data?.checkout_url) {
        track('start_checkout', { publicacao_id: checkoutSummary.publicacao_id });
        window.location.href = res.data.checkout_url;
        return;
      }
      setCheckoutError('Não foi possível abrir o pagamento.');
    } catch (err) {
      setCheckoutError(err.response?.data?.detail || 'Não foi possível abrir o pagamento.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const doSubmitCar = async () => {
    setError('');
    setNotice('');
    try {
      const res = await api.post('/marketplace/publicacoes/viatura', {
        ...carForm,
        idioma: lang,
        valor_semanal: Number(carForm.valor_semanal) || 0,
        preco_venda: carForm.finalidade === 'venda' ? Number(carForm.preco_venda) || 0 : null,
        quilometros: carForm.quilometros ? Number(carForm.quilometros) : null,
        ano: carForm.ano ? Number(carForm.ano) : null,
        duracao_dias: Number(carForm.duracao_dias) || 30,
      });
      const redirected = await triggerCheckoutIfNeeded(res.data);
      if (!redirected) {
        setPublishSuccess({
          expires_at: res.data?.expires_at,
          duracao_dias: res.data?.duracao_dias,
          tipo: 'viatura',
        });
        setCarForm(initialVehicleForm);
        setOpenForm(null);
        loadListings(tab, filters);
      } else {
        setOpenForm(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao submeter viatura.');
    }
  };

  const submitCar = (e) => {
    e.preventDefault();
    if (!carForm.verification_token) { setError('Verifique o seu email antes de submeter.'); return; }
    if (!carForm.phone_verification_token) { setError('Verifique o seu telemóvel (SMS) antes de submeter.'); return; }
    // Se o telefone fica visível, pede confirmação antes de submeter
    if (carForm.mostrar_telefone) {
      setConfirmSubmit({ type: 'viatura' });
      return;
    }
    doSubmitCar();
  };

  const doSubmitDriver = async () => {
    setError('');
    setNotice('');
    try {
      const res = await api.post('/marketplace/publicacoes/motorista', {
        ...driverForm,
        idioma: lang,
        valor_aluguer_maximo: driverForm.interesse === 'aluguer' && driverForm.valor_aluguer_maximo
          ? Number(driverForm.valor_aluguer_maximo)
          : null,
        pretende_dividir_carro: driverForm.pretende_dividir_carro,
        periodo_divisao: driverForm.pretende_dividir_carro ? driverForm.periodo_divisao : null,
        duracao_dias: Number(driverForm.duracao_dias) || 30,
      });
      const redirected = await triggerCheckoutIfNeeded(res.data);
      if (!redirected) {
        setPublishSuccess({
          expires_at: res.data?.expires_at,
          duracao_dias: res.data?.duracao_dias,
          tipo: 'motorista',
        });
        setDriverForm(initialDriverForm);
        setOpenForm(null);
        loadListings(tab, filters);
      } else {
        setOpenForm(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao submeter motorista.');
    }
  };

  const submitDriver = (e) => {
    e.preventDefault();
    if (!driverForm.verification_token) { setError('Verifique o seu email antes de submeter.'); return; }
    if (!driverForm.phone_verification_token) { setError('Verifique o seu telemóvel (SMS) antes de submeter.'); return; }
    if (driverForm.mostrar_telefone) {
      setConfirmSubmit({ type: 'motorista' });
      return;
    }
    doSubmitDriver();
  };

  // === Gestão dos próprios anúncios (self-service) ===
  const manageSendCode = async () => {
    const email = manageState.email.trim();
    if (!email) {
      setManageState((s) => ({ ...s, err: 'Indique o email usado para publicar.' }));
      return;
    }
    setManageState((s) => ({ ...s, loading: true, err: '', msg: '' }));
    try {
      await api.post('/marketplace/verificacao/enviar-codigo', { email });
      setManageState((s) => ({
        ...s,
        loading: false,
        step: 'code',
        msg: 'Código enviado para o seu email. Verifique a caixa de entrada e o spam.',
        err: '',
      }));
    } catch (err) {
      setManageState((s) => ({
        ...s,
        loading: false,
        err: err.response?.data?.detail || 'Falha ao enviar código.',
      }));
    }
  };

  const manageConfirmCode = async () => {
    const code = manageState.code.trim();
    const email = manageState.email.trim();
    if (!code) {
      setManageState((s) => ({ ...s, err: 'Introduza o código de 6 dígitos.' }));
      return;
    }
    setManageState((s) => ({ ...s, loading: true, err: '', msg: '' }));
    try {
      const verify = await api.post('/marketplace/verificacao/confirmar', { email, code });
      const token = verify.data.verification_token;
      const listRes = await api.post('/marketplace/meus-anuncios', {
        email,
        verification_token: token,
      });
      loadCommerce(email, token);
      setManageState((s) => ({
        ...s,
        loading: false,
        step: 'list',
        token,
        items: listRes.data.items || [],
        msg: '',
        err: listRes.data.items?.length === 0 ? 'Não foram encontrados anúncios com este email.' : '',
      }));
    } catch (err) {
      setManageState((s) => ({
        ...s,
        loading: false,
        err: err.response?.data?.detail || 'Código inválido ou expirado.',
      }));
    }
  };

  const loadCommerce = async (email, token) => {
    try {
      const res = await api.post('/marketplace/self/listings', { email, verification_token: token });
      const map = {};
      (res.data.items || []).forEach((it) => { map[it.listing_id] = it; });
      setCommerce(map);
    } catch (err) {
      setCommerce({});
    }
  };

  const commerceAction = async (listingId, acao) => {
    if (acao === 'arquivar' && !window.confirm('Arquivar este anúncio? Deixa de estar visível no marketplace.')) return;
    setManageState((s) => ({ ...s, loading: true, err: '', msg: '' }));
    try {
      const res = await api.post(`/marketplace/self/listings/${listingId}/estado`, {
        email: manageState.email.trim(), verification_token: manageState.token, acao,
      });
      setCommerce((c) => ({ ...c, [listingId]: res.data }));
      setManageState((s) => ({
        ...s,
        loading: false,
        msg: 'Anúncio atualizado.',
        items: s.items.map((it) => (it.id === listingId ? { ...it, status: res.data.estado_editorial } : it)),
      }));
      loadListings(tab, filters);
    } catch (err) {
      setManageState((s) => ({ ...s, loading: false, err: err.response?.data?.detail || 'Não foi possível atualizar o anúncio.' }));
    }
  };

  const commerceOrder = async (listingId, oferta) => {
    const confirmMsg = oferta.gratuito
      ? `Ativar "${oferta.name}" gratuitamente?`
      : `Comprar "${oferta.name}" por ${oferta.preco_final} ${oferta.moeda}?`;
    if (!window.confirm(confirmMsg)) return;
    setManageState((s) => ({ ...s, loading: true, err: '', msg: '' }));
    try {
      const res = await api.post(`/marketplace/self/listings/${listingId}/encomendar`, {
        email: manageState.email.trim(), verification_token: manageState.token, product_id: oferta.product_id,
      });
      await loadCommerce(manageState.email.trim(), manageState.token);
      setManageState((s) => ({
        ...s, loading: false,
        msg: res.data.ativado ? `Ativado: ${res.data.detalhe || oferta.name}` : (res.data.mensagem || 'Encomenda criada.'),
      }));
    } catch (err) {
      setManageState((s) => ({ ...s, loading: false, err: err.response?.data?.detail || 'Não foi possível concluir a compra.' }));
    }
  };

  const manageDeleteListing = async (publicacaoId) => {
    if (!window.confirm('Tem a certeza que quer apagar este anúncio? Esta acção não pode ser desfeita.')) {
      return;
    }
    setManageState((s) => ({ ...s, loading: true, err: '', msg: '' }));
    try {
      await api.delete(`/marketplace/publicacoes/${publicacaoId}/self`, {
        data: { email: manageState.email.trim(), verification_token: manageState.token },
      });
      setManageState((s) => ({
        ...s,
        loading: false,
        items: s.items.filter((it) => it.id !== publicacaoId),
        msg: 'Anúncio apagado com sucesso.',
        err: '',
      }));
      // refresh listagem pública
      loadListings(tab, filters);
    } catch (err) {
      setManageState((s) => ({
        ...s,
        loading: false,
        err: err.response?.data?.detail || 'Erro ao apagar anúncio.',
      }));
    }
  };

  const startEditListing = (item) => {
    const det = item.detalhes || {};
    const con = item.contacto || {};
    const venda = item.venda || {};
    setEditDraft({
      id: item.id,
      tipo: item.tipo,
      titulo: item.titulo || '',
      descricao: item.descricao || '',
      cidade: item.cidade || '',
      telefone: con.telefone || '',
      mostrar_telefone: con.mostrar_telefone !== false,
      mostrar_email: con.mostrar_email !== false,
      valor: item.tipo === 'viatura' ? (det.valor_semanal ?? '') : (det.valor_aluguer_maximo ?? ''),
      combustivel: det.combustivel || '',
      marca: det.marca || '',
      modelo: det.modelo || '',
      fotos: Array.isArray(det.fotos) ? det.fotos : [],
      fotosUploading: false,
      // campos específicos de venda (Fase 3)
      preco: item.tipo === 'venda' ? (venda.comercial?.preco ?? det.preco_venda ?? '') : '',
      quilometros: item.tipo === 'venda' ? (venda.viatura?.quilometros ?? det.quilometros ?? '') : '',
      estado_principal: venda.estado?.principal || 'operacional',
      categoria: venda.categoria || 'ex_tvde',
      preco_negociavel: !!venda.comercial?.preco_negociavel,
      venda_urgente: !!venda.comercial?.venda_urgente,
      equipamentos: venda.equipamentos || '',
      defeitos: venda.defeitos || '',
    });
  };

  const onEditPhotos = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setEditDraft((d) => (d ? { ...d, fotosUploading: true } : d));
    setManageState((s) => ({ ...s, err: '' }));
    try {
      const uploaded = [];
      for (const file of files) {
        try {
          uploaded.push(await uploadImage(file));
        } catch (uploadErr) {
          console.warn('Upload falhou, a usar base64 como fallback:', uploadErr);
          uploaded.push(await toBase64(file));
        }
      }
      setEditDraft((d) => {
        if (!d) return d;
        const merged = [...(d.fotos || []), ...uploaded].slice(0, 6);
        return { ...d, fotos: merged, fotosUploading: false };
      });
    } catch (err) {
      setEditDraft((d) => (d ? { ...d, fotosUploading: false } : d));
      setManageState((s) => ({ ...s, err: 'Erro ao carregar fotos. Tente novamente.' }));
    }
  };

  const removeEditPhoto = (idx) => {
    setEditDraft((d) => (d ? { ...d, fotos: (d.fotos || []).filter((_, i) => i !== idx) } : d));
  };

  const manageSaveEdit = async () => {
    if (!editDraft) return;
    setManageState((s) => ({ ...s, loading: true, err: '', msg: '' }));
    if (editDraft.tipo === 'venda') {
      try {
        await api.put(`/marketplace/venda/publicacoes/${editDraft.id}/self`, {
          email: manageState.email.trim(),
          verification_token: manageState.token,
          titulo: editDraft.titulo,
          descricao: editDraft.descricao,
          preco: editDraft.preco === '' ? undefined : Number(editDraft.preco),
          preco_negociavel: editDraft.preco_negociavel,
          venda_urgente: editDraft.venda_urgente,
          quilometros: editDraft.quilometros === '' ? undefined : Number(editDraft.quilometros),
          estado_principal: editDraft.estado_principal,
          categoria: editDraft.categoria,
          equipamentos: editDraft.equipamentos,
          defeitos: editDraft.defeitos,
          mostrar_telefone: editDraft.mostrar_telefone,
          mostrar_email: editDraft.mostrar_email,
          fotos: editDraft.fotos || [],
        });
        const listRes = await api.post('/marketplace/meus-anuncios', {
          email: manageState.email.trim(), verification_token: manageState.token,
        });
        setManageState((s) => ({ ...s, loading: false, items: listRes.data.items || [], msg: 'Anúncio de venda atualizado.', err: '' }));
        setEditDraft(null);
      } catch (err) {
        setManageState((s) => ({ ...s, loading: false, err: err.response?.data?.detail || 'Erro ao atualizar o anúncio de venda.' }));
      }
      return;
    }
    try {
      const detalhes = { combustivel: editDraft.combustivel };
      if (editDraft.tipo === 'viatura') {
        detalhes.marca = editDraft.marca;
        detalhes.modelo = editDraft.modelo;
        if (editDraft.valor !== '' && editDraft.valor !== null) detalhes.valor_semanal = Number(editDraft.valor);
      } else if (editDraft.valor !== '' && editDraft.valor !== null) {
        detalhes.valor_aluguer_maximo = Number(editDraft.valor);
      }
      detalhes.fotos = editDraft.fotos || [];
      const res = await api.put(`/marketplace/publicacoes/${editDraft.id}/self`, {
        email: manageState.email.trim(),
        verification_token: manageState.token,
        titulo: editDraft.titulo,
        descricao: editDraft.descricao,
        cidade: editDraft.cidade,
        telefone: editDraft.telefone,
        mostrar_telefone: editDraft.mostrar_telefone,
        mostrar_email: editDraft.mostrar_email,
        detalhes,
      });
      setManageState((s) => ({
        ...s,
        loading: false,
        items: s.items.map((it) => (it.id === editDraft.id ? res.data : it)),
        msg: 'Anúncio atualizado com sucesso.',
        err: '',
      }));
      setEditDraft(null);
      loadListings(tab, filters);
    } catch (err) {
      setManageState((s) => ({ ...s, loading: false, err: err.response?.data?.detail || 'Erro ao atualizar anúncio.' }));
    }
  };

  const openManageModal = () => {
    navigate('/gerir-anuncio');
  };

  const closeManageModal = () => {
    setManageState((s) => ({ ...s, open: false }));
    if (routeMode) navigate('/');
  };

  // Abre o lightbox com TODAS as fotos do anúncio. Se o card só tiver thumbnail,
  // vai buscar a galeria completa ao endpoint público de detalhe.
  const openLightbox = async (item) => {
    let fotos = (item?.detalhes?.fotos) || [];
    const fotosCount = item?.detalhes?.fotos_count ?? fotos.length;
    if (fotosCount > fotos.length && item?.id) {
      try {
        const res = await api.get(`/marketplace/publicacoes/${item.id}`);
        fotos = res.data?.detalhes?.fotos || fotos;
      } catch (e) {
        // ignora — usa o que houver
      }
    }
    if (!fotos.length) return;
    setLightbox({ open: true, fotos, idx: 0, title: item.titulo || '' });
  };
  const closeLightbox = () => setLightbox((s) => ({ ...s, open: false }));
  const lightboxPrev = () =>
    setLightbox((s) => ({ ...s, idx: (s.idx - 1 + s.fotos.length) % s.fotos.length }));
  const lightboxNext = () =>
    setLightbox((s) => ({ ...s, idx: (s.idx + 1) % s.fotos.length }));

  const onCarPhotos = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, 6);
    setError('');
    try {
      // Upload um a um, com fallback para base64 se o storage estiver em baixo
      const uploaded = [];
      for (const file of files) {
        try {
          const url = await uploadImage(file);
          uploaded.push(url);
        } catch (uploadErr) {
          console.warn('Upload falhou, a usar base64 como fallback:', uploadErr);
          uploaded.push(await toBase64(file));
        }
      }
      setCarForm((prev) => ({ ...prev, fotos: uploaded }));
    } catch (err) {
      setError('Erro ao carregar fotos. Tente novamente.');
    }
  };

  const sendLead = async (e) => {
    e.preventDefault();
    if (!selectedListing) return;
    if (!leadForm.verification_token) { setLeadState({ loading: false, msg: '', err: 'Verifique o seu email antes de contactar.' }); return; }
    if (leadForm.telefone && !leadForm.phone_verification_token) { setLeadState({ loading: false, msg: '', err: 'Indicou um telemóvel — verifique-o por SMS ou deixe o campo vazio.' }); return; }
    if (!leadForm.aceita_privacidade) { setLeadState({ loading: false, msg: '', err: 'Tem de aceitar a Política de Privacidade para enviar o contacto.' }); return; }
    setLeadState({ loading: true, msg: '', err: '' });
    try {
      await api.post('/marketplace/leads', {
        publicacao_id: selectedListing.id,
        nome: leadForm.nome,
        email: leadForm.email,
        telefone: leadForm.telefone,
        mensagem: leadForm.mensagem,
        email_verification_token: leadForm.verification_token,
        phone_verification_token: leadForm.phone_verification_token,
        aceita_privacidade: leadForm.aceita_privacidade,
        aceita_alertas: leadForm.aceita_alertas,
      });
      // Reutiliza a verificação de email neste navegador (menos fricção em contactos seguintes)
      try {
        localStorage.setItem('mp_lead_verify', JSON.stringify({ email: leadForm.email.trim().toLowerCase(), token: leadForm.verification_token, at: Date.now() }));
      } catch (_) {}
      track('send_contact', { publicacao_id: selectedListing.id, tipo: selectedListing.tipo });
      setLeadState({ loading: false, msg: 'Contacto enviado com sucesso.', err: '' });
      setLeadForm({ nome: '', email: '', telefone: '', mensagem: '', code: '', phone_code: '', verification_token: '', phone_verification_token: '', aceita_privacidade: false, aceita_alertas: false });
      setLeadEmailState({ sending: false, verifying: false, sent: false, verified: false, msg: '', err: '' });
      setLeadPhoneState({ sending: false, verifying: false, sent: false, verified: false, msg: '', err: '' });
      setTimeout(() => setSelectedListing(null), 800);
    } catch (err) {
      setLeadState({ loading: false, msg: '', err: err.response?.data?.detail || 'Erro ao enviar contacto.' });
    }
  };

  // Reaproveita verificação de email válida (até 24h) para não pedir código a cada contacto.
  const REUSE_MS = 24 * 60 * 60 * 1000;
  useEffect(() => {
    if (!selectedListing) return;
    const email = (leadForm.email || '').trim().toLowerCase();
    if (!email || leadForm.verification_token) return;
    try {
      const saved = JSON.parse(localStorage.getItem('mp_lead_verify') || 'null');
      if (saved && saved.email === email && saved.token && Date.now() - saved.at < REUSE_MS) {
        setLeadForm((prev) => ({ ...prev, verification_token: saved.token }));
        setLeadEmailState((s) => ({ ...s, verified: true, msg: 'Email já verificado neste dispositivo.' }));
      }
    } catch (_) {}
  }, [leadForm.email, selectedListing]);

  const openPublishOption = (type) => {
    navigate(type === 'viatura' ? '/anunciar-viatura' : '/publicar-procura');
  };

  return (
    <div
      className="relative min-h-screen bg-slate-50 text-slate-900"
      data-testid="marketplace-page"
    >
      <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-3 px-4 py-3 md:px-8 md:py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <img
              src="/icon-192x192.png"
              alt="Marketplace TVDE"
              className="h-10 w-10 shrink-0 rounded-xl object-cover shadow-sm ring-1 ring-slate-200"
              data-testid="marketplace-nav-icon"
            />
            <div className="min-w-0">
              <span className="block truncate text-base font-extrabold text-slate-900 md:text-lg" data-testid="marketplace-nav-title">{t.title}</span>
            </div>
          </div>

          {/* Mobile/tablet: botão hamburger (a navegação compacta entra antes de o nome cortar) */}
          <button
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="inline-flex shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white p-2.5 text-slate-700 transition hover:bg-slate-50 xl:hidden"
            data-testid="marketplace-nav-mobile-toggle"
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          {/* Desktop: botões inline */}
          <div className="relative hidden items-center gap-2 xl:flex">
            <button
              onClick={() => setLang((prev) => (prev === 'pt' ? 'en' : 'pt'))}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              data-testid="marketplace-language-toggle"
              aria-label="Mudar idioma"
            >
              <Languages className="h-4 w-4" /> {lang.toUpperCase()}
            </button>

            <Link
              to="/#viaturas"
              onClick={(e) => { e.preventDefault(); document.getElementById('marketplace-listings-anchor')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100"
              data-testid="marketplace-view-vehicles-btn"
            >
              Ver viaturas
            </Link>

            <Link
              to="/comprar-viaturas-tvde"
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50"
              data-testid="marketplace-buy-link"
            >
              Comprar
            </Link>

            <Link
              to="/parceiros"
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50"
              data-testid="marketplace-partners-link"
            >
              Parceiros
            </Link>

            <Link
              to="/precos"
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50"
              data-testid="marketplace-pricing-link"
            >
              Preços
            </Link>

            <Link
              to="/noticias-tvde"
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50"
              data-testid="marketplace-news-link"
            >
              Notícias TVDE
            </Link>

            <button
              onClick={openManageModal}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50"
              data-testid="marketplace-manage-btn"
              title="Gerir o meu anúncio"
            >
              <Settings className="h-4 w-4" /> Gerir
            </button>

            {/* Menu "Anunciar": agrupa todas as formas de publicação num único botão dominante */}
            <div className="relative" onMouseLeave={() => setPublishMenuOpen(false)}>
              <button
                onClick={() => setPublishMenuOpen((v) => !v)}
                onMouseEnter={() => setPublishMenuOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-3.5 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-emerald-600"
                data-testid="marketplace-publish-menu-btn"
                aria-haspopup="true"
                aria-expanded={publishMenuOpen}
              >
                <Plus className="h-4 w-4" /> Anunciar
              </button>
              {publishMenuOpen && (
                <div className="absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl"
                  data-testid="marketplace-publish-menu">
                  <button onClick={() => { setPublishMenuOpen(false); openPublishOption('viatura'); }}
                    className="block w-full px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-emerald-50"
                    data-testid="marketplace-publish-vehicle-btn">
                    Anunciar a minha viatura (aluguer)
                  </button>
                  <Link to="/vender-viatura" onClick={() => setPublishMenuOpen(false)}
                    className="block px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-emerald-50"
                    data-testid="marketplace-sell-link">
                    Vender uma viatura
                  </Link>
                  <button onClick={() => { setPublishMenuOpen(false); openPublishOption('motorista'); }}
                    className="block w-full px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-emerald-50"
                    data-testid="marketplace-publish-driver-btn">
                    Sou motorista — procuro viatura (gratuito)
                  </button>
                  <Link to="/adicionar-parceiro" onClick={() => setPublishMenuOpen(false)}
                    className="block px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-emerald-50"
                    data-testid="marketplace-add-partner-link">
                    Registar stand, associação ou serviço
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dropdown mobile */}
        {mobileMenuOpen && (
          <div
            className="border-t border-slate-200 bg-white xl:hidden"
            data-testid="marketplace-nav-mobile-menu"
          >
            <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-2 px-4 py-4">
              <button
                onClick={() => { openPublishOption('viatura'); setMobileMenuOpen(false); }}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600"
                data-testid="marketplace-publish-vehicle-btn-mobile"
              >
                <Plus className="h-4 w-4" /> Anunciar a minha Viatura
              </button>
              <button
                onClick={() => { openPublishOption('motorista'); setMobileMenuOpen(false); }}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
                data-testid="marketplace-publish-driver-btn-mobile"
              >
                <UserSearch className="h-4 w-4" /> Anunciar Procura de Viatura
              </button>
              <Link
                to="/#viaturas"
                onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); document.getElementById('marketplace-listings-anchor')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700"
                data-testid="marketplace-view-vehicles-btn-mobile"
              >
                Ver viaturas disponíveis
              </Link>
              <Link
                to="/comprar-viaturas-tvde"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800"
                data-testid="marketplace-buy-link-mobile"
              >
                Comprar viaturas
              </Link>
              <Link
                to="/vender-viatura"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800"
                data-testid="marketplace-sell-link-mobile"
              >
                Vender uma viatura
              </Link>
              <Link
                to="/parceiros"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800"
                data-testid="marketplace-partners-link-mobile"
              >
                Parceiros e serviços
              </Link>
              <Link
                to="/noticias-tvde"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800"
                data-testid="marketplace-news-link-mobile"
              >
                Notícias TVDE
              </Link>
              <button
                onClick={openManageModal}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"
                data-testid="marketplace-manage-btn-mobile"
              >
                <Settings className="h-4 w-4" /> Gerir o meu anúncio
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setLang((prev) => (prev === 'pt' ? 'en' : 'pt'))}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="marketplace-language-toggle-mobile"
                >
                  <Languages className="h-4 w-4" /> {lang.toUpperCase()}
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main className="relative w-full pb-16" data-testid="marketplace-main">
        {routeMode && (
          <div className="mx-auto w-full max-w-[1400px] px-4 pt-4 md:px-8">
            <button
              onClick={() => navigate('/')}
              data-testid="route-back-home"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              ← Voltar ao Marketplace
            </button>
          </div>
        )}
        {mpSettings?.banner_promocional && (
          <div
            className="animate-fade-in mx-auto mb-0 mt-0 w-full max-w-[1400px] rounded-none border-y border-emerald-200 bg-emerald-50 px-5 py-3 text-center text-sm font-semibold text-emerald-800 md:my-4 md:rounded-2xl"
            data-testid="marketplace-promo-banner"
          >
            🎉 {mpSettings.banner_promocional}
          </div>
        )}

        {/* HERO PREMIUM — claro, profissional */}
        {!routeMode && (
          <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
            <AdSlot posicao="homepage_top" pagina="/" />
          </div>
        )}
        {!routeMode && (
        <PremiumHero>
          <div className="grid items-center gap-8 lg:grid-cols-[50%_50%] lg:gap-6 xl:grid-cols-[45%_55%]">
            <div className="relative z-10">
              <div className="flex flex-wrap items-center gap-2" data-testid="marketplace-hero-badges">
                <span
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"
                  data-testid="marketplace-hero-eyebrow"
                >
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                  Marketplace TVDE · Portugal
                </span>
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm"
                  data-testid="marketplace-hero-badge-premium"
                >
                  <Award className="h-3.5 w-3.5 text-amber-300" /> Marketplace dedicado exclusivamente ao TVDE em Portugal
                </span>
              </div>
              <h1
                className="animate-slide-up mt-4 font-extrabold tracking-tight text-slate-900"
                style={{ fontSize: 'clamp(38px, 4.4vw, 86px)', lineHeight: 0.99 }}
                data-testid="marketplace-hero-title"
              >
                {cms('home_hero_titulo') ? (
                  cms('home_hero_titulo')
                ) : (
                  <>
                    O marketplace
                    <br />
                    <span className="text-emerald-600">TVDE</span> de Portugal
                  </>
                )}
              </h1>
              <p
                className="mt-4 max-w-xl text-base text-slate-600 md:text-lg"
                data-testid="marketplace-hero-subtitle"
              >
                {cms('home_hero_subtitulo') ? (
                  cms('home_hero_subtitulo')
                ) : (
                  <>
                    Anuncie viaturas, encontre motoristas e ligue investidores ao mercado{' '}
                    <strong className="text-slate-900">TVDE</strong> em Portugal.
                  </>
                )}
              </p>

              {mpSettings?.texto_comercial && (
                <p
                  className="mt-3 max-w-xl whitespace-pre-wrap text-sm text-slate-500"
                  data-testid="marketplace-commercial-text"
                >
                  {mpSettings.comercio_ativo
                    ? mpSettings.texto_comercial
                    : `${mpSettings.texto_comercial.split(/pagamento/i)[0].trim()} Métodos de pagamento disponíveis quando terminar o período gratuito.`}
                </p>
              )}

              <PlatformBadges pagamentosAtivos={!!mpSettings?.comercio_ativo} />

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => openPublishOption('viatura')}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600 hover:shadow-md"
                  data-testid="marketplace-hero-cta-vehicle"
                >
                  <Car className="h-4 w-4" /> Anunciar a minha Viatura
                </button>
                <button
                  onClick={() => openPublishOption('motorista')}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50"
                  data-testid="marketplace-hero-cta-driver"
                >
                  <UserSearch className="h-4 w-4" /> Anunciar Procura de Viatura
                </button>
              </div>

              {mpSettings && !mpSettings.campanha_gratuita_ativa && mpSettings.comercio_ativo && (
                <p
                  className="mt-6 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm"
                  data-testid="marketplace-pricing-badge"
                >
                  💳 Anúncio: {Number(mpSettings.preco_unico).toFixed(2)} {mpSettings.moeda} · {mpSettings.duracao_dias} dias · Cartão / MBWay / Multibanco
                </p>
              )}
              {mpSettings?.campanha_gratuita_ativa && (
                <p
                  className="mt-5 inline-flex max-w-[560px] items-center gap-2 rounded-2xl border border-amber-200/90 bg-amber-50/70 px-4 py-3 text-sm font-semibold text-amber-800 shadow-sm"
                  data-testid="marketplace-free-launch-banner"
                >
                  🎁 Lançamento gratuito · Anuncie viaturas e motoristas sem custos durante {mpSettings.campanha_gratuita_dias} dias
                </p>
              )}

              <p
                className="mt-2.5 flex flex-wrap items-center gap-2 text-xs text-slate-400"
                data-testid="marketplace-payments-note"
              >
                <Lock className="h-3.5 w-3.5 text-emerald-500" />
                {mpSettings?.comercio_ativo
                  ? 'Pagamentos seguros · MB Way · Cartão · Multibanco'
                  : 'Publicar é gratuito neste momento · Métodos de pagamento disponíveis quando terminar o período gratuito'}
              </p>
            </div>

            {/* Zona visual: mapa decorativo (absoluto) + card flutuante */}
            <HeroComposition>
              <ConfidenceCard pagamentosAtivos={!!mpSettings?.comercio_ativo} />
            </HeroComposition>
          </div>
        </PremiumHero>
        )}

        {!routeMode && <InstitutionalStrip />}

        <div className="relative mx-auto w-full max-w-[1400px] space-y-8 px-4 pt-10 md:px-8">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6" data-testid="marketplace-controls" style={{ display: routeMode ? 'none' : undefined }}>
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3">
            <button
              onClick={() => setTab('viatura')}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${tab === 'viatura' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="marketplace-tab-cars"
            >
              <Car className="h-4 w-4" /> {t.tabCars}
            </button>
            <button
              onClick={() => setTab('motorista')}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${tab === 'motorista' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid="marketplace-tab-drivers"
            >
              <UserSearch className="h-4 w-4" /> {t.tabDrivers}
            </button>
            <Link
              to="/comprar-viaturas-tvde"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-50"
              data-testid="marketplace-tab-venda"
            >
              <Car className="h-4 w-4" /> Comprar e vender viaturas →
            </Link>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={filters.cidade}
                onChange={(e) => setFilters((prev) => ({ ...prev, cidade: e.target.value }))}
                placeholder={t.city}
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/20"
                data-testid="marketplace-filter-city"
              />
            </div>

            {tab === 'viatura' && (
              <select
                value={filters.combustivel}
                onChange={(e) => setFilters((prev) => ({ ...prev, combustivel: e.target.value }))}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/20"
                data-testid="marketplace-filter-fuel"
              >
                <option value="">{t.fuel}</option>
                <option value="Elétrico">Elétrico</option>
                <option value="Híbrido">Híbrido</option>
                <option value="Combustão">Combustão</option>
                <option value="GPL">GPL</option>
              </select>
            )}

            {tab === 'viatura' && (
              <input
                type="number"
                value={filters.precoMax}
                onChange={(e) => setFilters((prev) => ({ ...prev, precoMax: e.target.value }))}
                placeholder={t.maxPrice}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/20"
                data-testid="marketplace-filter-max-price"
              />
            )}

            <button
              onClick={() => loadListings(tab, filters)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600 hover:shadow-md"
              data-testid="marketplace-apply-filters"
            >
              <Filter className="h-4 w-4" /> {t.apply}
            </button>
          </div>
        </section>

        {openForm === 'viatura' && (
          <section className="rounded-3xl border border-emerald-200 bg-white p-6 shadow-sm" data-testid="marketplace-car-form-section">
            <h3 className="mb-4 text-xl font-bold text-slate-900">{t.openCarForm}</h3>
            <form onSubmit={submitCar} className="grid gap-3 md:grid-cols-2">
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Primeiro nome" value={carForm.primeiro_nome} onChange={(e) => setCarForm((prev) => ({ ...prev, primeiro_nome: e.target.value }))} data-testid="car-form-first-name" required />
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Último nome" value={carForm.ultimo_nome} onChange={(e) => setCarForm((prev) => ({ ...prev, ultimo_nome: e.target.value }))} data-testid="car-form-last-name" required />
              <input className="rounded-xl border border-slate-300 px-3 py-2" type="email" placeholder="Email" value={carForm.email} onChange={(e) => setCarForm((prev) => ({ ...prev, email: e.target.value }))} data-testid="car-form-email" required />
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Telefone" value={carForm.telefone} onChange={(e) => setCarForm((prev) => ({ ...prev, telefone: e.target.value }))} data-testid="car-form-phone" required />

              <div className="md:col-span-2 space-y-2">
                <div className="flex gap-2 items-stretch">
                  <button type="button" onClick={() => sendEmailCode(carForm.email)} disabled={codeState.sending} className="rounded-xl border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-60" data-testid="car-form-send-code">{codeState.sending ? 'A enviar...' : (codeState.sent ? '✓ Reenviar' : t.sendCode)}</button>
                  <input className="flex-1 rounded-xl border border-slate-300 px-3 py-2" placeholder="Código de 6 dígitos" value={carForm.code} onChange={(e) => setCarForm((prev) => ({ ...prev, code: e.target.value }))} data-testid="car-form-code" />
                  <button type="button" onClick={verifyCarCode} disabled={codeState.verifying || codeState.verified} className={`rounded-xl border px-4 py-2 text-sm font-semibold disabled:opacity-60 ${codeState.verified ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-cyan-300 text-cyan-700'}`} data-testid="car-form-verify-code">{codeState.verified ? '✓ Validado' : (codeState.verifying ? 'A verificar...' : t.verifyCode)}</button>
                </div>
                {codeState.msg && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="car-code-success">{codeState.msg}</div>}
                {codeState.err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="car-code-error">{codeState.err}</div>}
              </div>

              <div className="md:col-span-2 space-y-2 rounded-xl border border-dashed border-cyan-300 bg-cyan-50/50 p-3">
                <p className="text-xs font-semibold text-slate-600">Verificação do telemóvel (obrigatória)</p>
                <div className="flex gap-2 items-stretch">
                  <button type="button" onClick={() => sendPhoneCode(carForm.telefone, phoneCodeState, setPhoneCodeState)} disabled={phoneCodeState.sending} className="rounded-xl border border-cyan-300 px-4 py-2 text-sm font-semibold text-cyan-700 disabled:opacity-60" data-testid="car-form-send-sms">{phoneCodeState.sending ? 'A enviar...' : (phoneCodeState.sent ? '✓ Reenviar SMS' : 'Enviar SMS')}</button>
                  <input className="flex-1 rounded-xl border border-slate-300 px-3 py-2" placeholder="Código do SMS" value={carForm.phone_code} onChange={(e) => setCarForm((prev) => ({ ...prev, phone_code: e.target.value }))} data-testid="car-form-sms-code" />
                  <button type="button" onClick={() => verifyPhoneCode(carForm.telefone, carForm.phone_code, setCarForm, 'phone_verification_token', setPhoneCodeState)} disabled={phoneCodeState.verifying || phoneCodeState.verified} className={`rounded-xl border px-4 py-2 text-sm font-semibold disabled:opacity-60 ${phoneCodeState.verified ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-cyan-300 text-cyan-700'}`} data-testid="car-form-verify-sms">{phoneCodeState.verified ? '✓ Validado' : (phoneCodeState.verifying ? 'A verificar...' : 'Validar SMS')}</button>
                </div>
                {phoneCodeState.msg && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="car-sms-success">{phoneCodeState.msg}</div>}
                {phoneCodeState.err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="car-sms-error">{phoneCodeState.err}</div>}
              </div>

              <p className="md:col-span-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="car-form-sale-notice">
                Este formulário é para <strong>arrendar</strong> a sua viatura. Para <strong>vender</strong>, use a
                área dedicada:{' '}
                <Link to="/vender-viatura" className="font-bold underline" data-testid="car-form-sale-link">Vender uma viatura</Link>.
              </p>
              <input className="rounded-xl border border-slate-300 px-3 py-2 md:col-span-2" placeholder="Título do anúncio" value={carForm.titulo} onChange={(e) => setCarForm((prev) => ({ ...prev, titulo: e.target.value }))} data-testid="car-form-title" required />
              <textarea className="rounded-xl border border-slate-300 px-3 py-2 md:col-span-2" rows={3} placeholder="Descrição" value={carForm.descricao} onChange={(e) => setCarForm((prev) => ({ ...prev, descricao: e.target.value }))} data-testid="car-form-description" />

              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Cidade" value={carForm.cidade} onChange={(e) => setCarForm((prev) => ({ ...prev, cidade: e.target.value }))} data-testid="car-form-city" required />
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Marca" value={carForm.marca} onChange={(e) => setCarForm((prev) => ({ ...prev, marca: e.target.value }))} data-testid="car-form-brand" required />
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Modelo" value={carForm.modelo} onChange={(e) => setCarForm((prev) => ({ ...prev, modelo: e.target.value }))} data-testid="car-form-model" required />
              <input type="number" className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Valor semanal de aluguer" value={carForm.valor_semanal} onChange={(e) => setCarForm((prev) => ({ ...prev, valor_semanal: e.target.value }))} data-testid="car-form-price" required />

              <select className="rounded-xl border border-slate-300 px-3 py-2" value={carForm.combustivel} onChange={(e) => setCarForm((prev) => ({ ...prev, combustivel: e.target.value }))} data-testid="car-form-fuel">
                <option>Combustão</option>
                <option>GPL</option>
                <option>Elétrico</option>
                <option>Híbrido</option>
              </select>
              <select className="rounded-xl border border-slate-300 px-3 py-2" value={carForm.caixa} onChange={(e) => setCarForm((prev) => ({ ...prev, caixa: e.target.value }))} data-testid="car-form-gearbox">
                <option>Automática</option>
                <option>Manual</option>
              </select>

              <p className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="car-form-contact-privacy-note">
                Por defeito os seus contactos ficam <strong>privados</strong>. Se ativar as opções abaixo, o contacto selecionado fica <strong>publicamente visível</strong> no anúncio. Pode manter tudo privado e receber mensagens através do Marketplace.
              </p>

              <label className="md:col-span-2 flex items-center gap-2 rounded-xl border border-slate-300 bg-emerald-50 px-3 py-2 text-sm" data-testid="car-form-phone-visibility">
                <input type="checkbox" checked={carForm.mostrar_telefone} onChange={(e) => setCarForm((prev) => ({ ...prev, mostrar_telefone: e.target.checked }))} data-testid="car-form-phone-visibility-checkbox" />
                Mostrar telefone no anúncio (opcional)
              </label>

              <label className="md:col-span-2 flex items-center gap-2 rounded-xl border border-slate-300 bg-emerald-50 px-3 py-2 text-sm" data-testid="car-form-email-visibility">
                <input type="checkbox" checked={carForm.mostrar_email} onChange={(e) => setCarForm((prev) => ({ ...prev, mostrar_email: e.target.checked }))} data-testid="car-form-email-visibility-checkbox" />
                Mostrar email no anúncio (opcional)
              </label>

              <label className="md:col-span-2 rounded-xl border border-dashed border-emerald-300 px-4 py-4 text-sm" data-testid="car-form-photo-upload-label">
                <span className="mb-2 inline-flex items-center gap-2"><ImagePlus className="h-4 w-4" /> Adicionar fotos do carro (até 6)</span>
                <input type="file" accept="image/*" multiple onChange={onCarPhotos} className="mt-2 block text-xs" data-testid="car-form-photo-upload" />
              </label>

              {/* Duração do anúncio (campanha gratuita) */}
              <div className="md:col-span-2 rounded-xl border border-slate-200 bg-emerald-50/40 p-3">
                <p className="text-xs font-semibold text-slate-700">Duração do anúncio</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {[15, 30].map((dias) => (
                    <label
                      key={dias}
                      className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${carForm.duracao_dias === dias ? 'border-emerald-500 bg-emerald-500 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300'}`}
                      data-testid={`car-form-duration-${dias}`}
                    >
                      <input
                        type="radio"
                        name="car-duracao"
                        value={dias}
                        checked={carForm.duracao_dias === dias}
                        onChange={() => setCarForm((p) => ({ ...p, duracao_dias: dias }))}
                        className="sr-only"
                      />
                      {dias} dias
                    </label>
                  ))}
                </div>
                <p className="mt-1.5 text-[11px] text-slate-500">
                  O anúncio será automaticamente removido do site após este período.
                </p>
              </div>

              <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-emerald-500"
                    data-testid="car-form-accept-terms"
                  />
                  <span>
                    Confirmo que sou o legítimo proprietário/representante da viatura anunciada e
                    aceito os <Link to="/termos" target="_blank" className="font-semibold text-emerald-600 underline">Termos</Link>, a{' '}
                    <Link to="/privacidade" target="_blank" className="font-semibold text-emerald-600 underline">Política de Privacidade</Link> e a{' '}
                    <Link to="/cookies" target="_blank" className="font-semibold text-emerald-600 underline">Política de Cookies</Link>.
                  </span>
                </label>
              </div>
              <button type="submit" disabled={!acceptedTerms} className="md:col-span-2 rounded-xl bg-emerald-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none" data-testid="car-form-submit">{t.submitCar}</button>
            </form>
          </section>
        )}

        {openForm === 'motorista' && (
          <section className="rounded-3xl border border-cyan-200 bg-white p-6 shadow-sm" data-testid="marketplace-driver-form-section">
            <h3 className="mb-4 text-xl font-bold text-slate-900">{t.openDriverForm}</h3>
            <form onSubmit={submitDriver} className="grid gap-3 md:grid-cols-2">
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Primeiro nome" value={driverForm.primeiro_nome} onChange={(e) => setDriverForm((prev) => ({ ...prev, primeiro_nome: e.target.value }))} data-testid="driver-form-first-name" required />
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Último nome" value={driverForm.ultimo_nome} onChange={(e) => setDriverForm((prev) => ({ ...prev, ultimo_nome: e.target.value }))} data-testid="driver-form-last-name" required />
              <input className="rounded-xl border border-slate-300 px-3 py-2" type="email" placeholder="Email" value={driverForm.email} onChange={(e) => setDriverForm((prev) => ({ ...prev, email: e.target.value }))} data-testid="driver-form-email" required />
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Telefone" value={driverForm.telefone} onChange={(e) => setDriverForm((prev) => ({ ...prev, telefone: e.target.value }))} data-testid="driver-form-phone" required />

              <div className="md:col-span-2 space-y-2">
                <div className="flex gap-2 items-stretch">
                  <button type="button" onClick={() => sendEmailCode(driverForm.email)} disabled={codeState.sending} className="rounded-xl border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-60" data-testid="driver-form-send-code">{codeState.sending ? 'A enviar...' : (codeState.sent ? '✓ Reenviar' : t.sendCode)}</button>
                  <input className="flex-1 rounded-xl border border-slate-300 px-3 py-2" placeholder="Código de 6 dígitos" value={driverForm.code} onChange={(e) => setDriverForm((prev) => ({ ...prev, code: e.target.value }))} data-testid="driver-form-code" />
                  <button type="button" onClick={verifyDriverCode} disabled={codeState.verifying || codeState.verified} className={`rounded-xl border px-4 py-2 text-sm font-semibold disabled:opacity-60 ${codeState.verified ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-cyan-300 text-cyan-700'}`} data-testid="driver-form-verify-code">{codeState.verified ? '✓ Validado' : (codeState.verifying ? 'A verificar...' : t.verifyCode)}</button>
                </div>
                {codeState.msg && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="driver-code-success">{codeState.msg}</div>}
                {codeState.err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="driver-code-error">{codeState.err}</div>}
              </div>

              <div className="md:col-span-2 space-y-2 rounded-xl border border-dashed border-cyan-300 bg-cyan-50/50 p-3">
                <p className="text-xs font-semibold text-slate-600">Verificação do telemóvel (obrigatória)</p>
                <div className="flex gap-2 items-stretch">
                  <button type="button" onClick={() => sendPhoneCode(driverForm.telefone, phoneCodeState, setPhoneCodeState)} disabled={phoneCodeState.sending} className="rounded-xl border border-cyan-300 px-4 py-2 text-sm font-semibold text-cyan-700 disabled:opacity-60" data-testid="driver-form-send-sms">{phoneCodeState.sending ? 'A enviar...' : (phoneCodeState.sent ? '✓ Reenviar SMS' : 'Enviar SMS')}</button>
                  <input className="flex-1 rounded-xl border border-slate-300 px-3 py-2" placeholder="Código do SMS" value={driverForm.phone_code} onChange={(e) => setDriverForm((prev) => ({ ...prev, phone_code: e.target.value }))} data-testid="driver-form-sms-code" />
                  <button type="button" onClick={() => verifyPhoneCode(driverForm.telefone, driverForm.phone_code, setDriverForm, 'phone_verification_token', setPhoneCodeState)} disabled={phoneCodeState.verifying || phoneCodeState.verified} className={`rounded-xl border px-4 py-2 text-sm font-semibold disabled:opacity-60 ${phoneCodeState.verified ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-cyan-300 text-cyan-700'}`} data-testid="driver-form-verify-sms">{phoneCodeState.verified ? '✓ Validado' : (phoneCodeState.verifying ? 'A verificar...' : 'Validar SMS')}</button>
                </div>
                {phoneCodeState.msg && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="driver-sms-success">{phoneCodeState.msg}</div>}
                {phoneCodeState.err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="driver-sms-error">{phoneCodeState.err}</div>}
              </div>

              <select className="rounded-xl border border-slate-300 px-3 py-2" value={driverForm.interesse} onChange={(e) => setDriverForm((prev) => ({ ...prev, interesse: e.target.value }))} data-testid="driver-form-interest">
                <option value="aluguer">Interesse em Aluguer</option>
                <option value="comissao">Interesse em Comissão</option>
              </select>
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Cidade" value={driverForm.cidade} onChange={(e) => setDriverForm((prev) => ({ ...prev, cidade: e.target.value }))} data-testid="driver-form-city" required />

              <select className="rounded-xl border border-slate-300 px-3 py-2" value={driverForm.tipo_viatura} onChange={(e) => setDriverForm((prev) => ({ ...prev, tipo_viatura: e.target.value }))} data-testid="driver-form-vehicle-type">
                <option>Citadino</option>
                <option>XL</option>
                <option>Van</option>
                <option>Carrinha</option>
              </select>

              <select className="rounded-xl border border-slate-300 px-3 py-2" value={driverForm.combustivel} onChange={(e) => setDriverForm((prev) => ({ ...prev, combustivel: e.target.value }))} data-testid="driver-form-fuel">
                <option>Combustão</option>
                <option>GPL</option>
                <option>Elétrico</option>
                <option>Híbrido</option>
              </select>

              {driverForm.interesse === 'aluguer' && (
                <input type="number" className="rounded-xl border border-slate-300 px-3 py-2 md:col-span-2" placeholder="Valor Aluguer máximo" value={driverForm.valor_aluguer_maximo} onChange={(e) => setDriverForm((prev) => ({ ...prev, valor_aluguer_maximo: e.target.value }))} data-testid="driver-form-max-rent" />
              )}

              <div className="md:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-slate-700">Disponibilidade</label>
                <select className="w-full rounded-xl border border-slate-300 px-3 py-2" value={driverForm.disponibilidade} onChange={(e) => setDriverForm((prev) => ({ ...prev, disponibilidade: e.target.value }))} data-testid="driver-form-availability">
                  <option value="">— Escolha uma opção —</option>
                  <option value="Imediata">Imediata</option>
                  <option value="Data a acordar">Data a acordar</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Tipo de turno</label>
                <select className="w-full rounded-xl border border-slate-300 px-3 py-2" value={driverForm.tipo_turno} onChange={(e) => setDriverForm((prev) => ({ ...prev, tipo_turno: e.target.value }))} data-testid="driver-form-type">
                  <option value="Tempo inteiro">Tempo inteiro</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Dia">Dia</option>
                  <option value="Noite">Noite</option>
                </select>
              </div>

              <select className="rounded-xl border border-slate-300 px-3 py-2 md:col-span-2" value={driverForm.pretende_dividir_carro ? 'sim' : 'nao'} onChange={(e) => setDriverForm((prev) => ({ ...prev, pretende_dividir_carro: e.target.value === 'sim' }))} data-testid="driver-form-share-car">
                <option value="nao">Pretende dividir carro com outro motorista? Não</option>
                <option value="sim">Pretende dividir carro com outro motorista? Sim</option>
              </select>

              {driverForm.pretende_dividir_carro && (
                <select className="rounded-xl border border-slate-300 px-3 py-2 md:col-span-2" value={driverForm.periodo_divisao} onChange={(e) => setDriverForm((prev) => ({ ...prev, periodo_divisao: e.target.value }))} data-testid="driver-form-share-period">
                  <option value="">Escolher período para dividir</option>
                  <option value="Dia">Dia</option>
                  <option value="Noite">Noite</option>
                </select>
              )}

              <textarea className="rounded-xl border border-slate-300 px-3 py-2 md:col-span-2" rows={3} placeholder="Mensagem opcional" value={driverForm.mensagem} onChange={(e) => setDriverForm((prev) => ({ ...prev, mensagem: e.target.value }))} data-testid="driver-form-message" />

              <p className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="driver-form-contact-privacy-note">
                Por defeito os seus contactos ficam <strong>privados</strong>. Se ativar as opções abaixo, o contacto selecionado fica <strong>publicamente visível</strong> no anúncio. Pode manter tudo privado e receber mensagens através do Marketplace.
              </p>

              <label className="md:col-span-2 flex items-center gap-2 rounded-xl border border-slate-300 bg-cyan-50 px-3 py-2 text-sm" data-testid="driver-form-phone-visibility">
                <input type="checkbox" checked={driverForm.mostrar_telefone} onChange={(e) => setDriverForm((prev) => ({ ...prev, mostrar_telefone: e.target.checked }))} data-testid="driver-form-phone-visibility-checkbox" />
                Mostrar telefone no anúncio (opcional)
              </label>

              <label className="md:col-span-2 flex items-center gap-2 rounded-xl border border-slate-300 bg-cyan-50 px-3 py-2 text-sm" data-testid="driver-form-email-visibility">
                <input type="checkbox" checked={driverForm.mostrar_email} onChange={(e) => setDriverForm((prev) => ({ ...prev, mostrar_email: e.target.checked }))} data-testid="driver-form-email-visibility-checkbox" />
                Mostrar email no anúncio (opcional)
              </label>

              {/* Duração do anúncio (campanha gratuita) */}
              <div className="md:col-span-2 rounded-xl border border-slate-200 bg-cyan-50/40 p-3">
                <p className="text-xs font-semibold text-slate-700">Duração do anúncio</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {[15, 30].map((dias) => (
                    <label
                      key={dias}
                      className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${driverForm.duracao_dias === dias ? 'border-cyan-500 bg-cyan-500 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-700 hover:border-cyan-300'}`}
                      data-testid={`driver-form-duration-${dias}`}
                    >
                      <input
                        type="radio"
                        name="driver-duracao"
                        value={dias}
                        checked={driverForm.duracao_dias === dias}
                        onChange={() => setDriverForm((p) => ({ ...p, duracao_dias: dias }))}
                        className="sr-only"
                      />
                      {dias} dias
                    </label>
                  ))}
                </div>
                <p className="mt-1.5 text-[11px] text-slate-500">
                  O anúncio será automaticamente removido do site após este período.
                </p>
              </div>

              <button type="submit" className="md:col-span-2 rounded-xl bg-cyan-500 px-4 py-3 font-semibold text-white hover:bg-cyan-600" data-testid="driver-form-submit">{t.submitDriver}</button>
            </form>
          </section>
        )}

        {notice && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="marketplace-success-message">{notice}</div>}
        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid="marketplace-error-message">{error}</div>}

        {!routeMode && (highlightedListings.length > 0 || saleHighlights.length > 0) && (
          <section className="space-y-4" data-testid="marketplace-highlights-section">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h3 className="text-2xl font-extrabold text-slate-900 md:text-3xl" data-testid="marketplace-highlights-title">
                  Oportunidades em destaque
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Espaços promovidos pelos anunciantes. O destaque é comercial e não representa uma avaliação da viatura.
                </p>
              </div>
              <Link
                to="/comprar-viaturas-tvde"
                className="text-sm font-bold text-amber-700 underline decoration-amber-300 underline-offset-4"
                data-testid="marketplace-sale-area-link"
              >
                Ver viaturas à venda →
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {highlightedListings.slice(0, 4).map((item) => (
                <Link
                  key={item.id}
                  to={listingUrl(item)}
                  className="flex flex-col gap-2 rounded-2xl border border-amber-300 bg-white p-4 shadow-sm ring-1 ring-amber-200 transition hover:-translate-y-0.5 hover:shadow-md"
                  data-testid={`marketplace-highlight-${item.id}`}
                >
                  <PromoBadges promocoes={item.promocoes} testIdPrefix={`highlight-${item.id}`} />
                  <p className="text-sm font-bold text-slate-900">{item.titulo}</p>
                  <p className="text-xs text-slate-500">{item.cidade}</p>
                </Link>
              ))}
              {saleHighlights.slice(0, 4).map((item) => (
                <Link
                  key={item.id}
                  to={`/comprar-viaturas-tvde/${item.id}`}
                  className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                  data-testid={`marketplace-sale-highlight-${item.id}`}
                >
                  <span className="w-fit rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase text-white">{item.categoria_label}</span>
                  <p className="text-sm font-bold text-slate-900">{item.titulo}</p>
                  <p className="text-sm font-extrabold text-slate-900">{Number(item.preco || 0).toLocaleString('pt-PT')} €</p>
                  <p className="text-xs text-slate-500">{item.localizacao}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <span id="marketplace-listings-anchor" className="block h-0 scroll-mt-24" aria-hidden="true" />
        <section className="space-y-5" data-testid="marketplace-listings-section" style={{ display: routeMode ? 'none' : undefined }}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h3 className="text-2xl font-extrabold text-slate-900 md:text-3xl" data-testid="marketplace-listings-title">
              {tab === 'viatura' ? t.carsList : tab === 'venda' ? t.salesList : t.driversList}
            </h3>
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {listings.length} {listings.length === 1 ? 'resultado' : 'resultados'}
            </span>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm" data-testid="marketplace-loading-state">
              {t.loading}
            </div>
          ) : listError ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center shadow-sm" data-testid="marketplace-list-error-state">
              <p className="text-base font-bold text-amber-900">{t.loadError}</p>
              <button
                onClick={() => loadListings(tab, filters)}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-amber-600"
                data-testid="marketplace-list-retry-button"
              >
                {t.retry}
              </button>
            </div>
          ) : listings.length === 0 ? (
            <div
              className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm"
              data-testid="marketplace-empty-state"
            >
              <div className="relative mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                <Car className="h-7 w-7" />
              </div>
              <p className="relative text-lg font-bold text-slate-900">
                Ainda não existem publicações disponíveis.
              </p>
              <p className="relative mt-1 text-sm text-slate-500">
                Sê o primeiro a publicar uma viatura ou um perfil de motorista.
              </p>
              <div className="relative mt-5 flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={() => openPublishOption('viatura')}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600"
                  data-testid="marketplace-empty-cta-vehicle"
                >
                  <Plus className="h-4 w-4" /> Publicar primeira viatura
                </button>
                <button
                  onClick={() => openPublishOption('motorista')}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50"
                  data-testid="marketplace-empty-cta-driver"
                >
                  <UserSearch className="h-4 w-4" /> Anunciar Procura de Viatura
                </button>
              </div>
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="marketplace-listings-grid">
              {listings.map((item) => (
                <article
                  key={item.id}
                  className={`group relative overflow-hidden rounded-2xl border bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-md ${
                    (item.promocoes || []).some((p) => p === 'destaque' || p === 'destaque_homepage')
                      ? 'border-amber-300 ring-1 ring-amber-200'
                      : 'border-slate-200 hover:border-emerald-200'
                  }`}
                  data-testid={`marketplace-listing-card-${item.id}`}
                >
                  {item.tipo === 'viatura' && item.detalhes?.fotos?.[0] && (
                    <div className="relative h-44 overflow-hidden bg-slate-100">
                      <button
                        type="button"
                        onClick={() => openLightbox(item)}
                        className="block h-full w-full cursor-zoom-in"
                        data-testid={`marketplace-listing-image-btn-${item.id}`}
                        aria-label="Ver fotos em ecrã inteiro"
                      >
                        <img
                          src={resolveImageUrl(item.detalhes.fotos[0])}
                          alt={item.titulo}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          data-testid={`marketplace-listing-image-${item.id}`}
                        />
                      </button>
                      <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm" title="Analisado pela equipa antes da publicação. Não é garantia da viatura.">
                          <BadgeCheck className="h-3 w-3" /> Anúncio revisto
                        </span>
                        {item.contacto?.verificado && (
                          <span className="inline-flex items-center rounded-md bg-teal-600 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm" title="Email/telemóvel do anunciante confirmados.">
                            Contacto confirmado
                          </span>
                        )}
                        <span className="inline-flex items-center rounded-md bg-cyan-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
                          TVDE
                        </span>
                      </div>
                      {(item.detalhes?.fotos_count ?? 1) > 1 && (
                        <div className="pointer-events-none absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-1 text-[10px] font-semibold text-white">
                          +{(item.detalhes?.fotos_count ?? 1)} fotos
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-3 p-5">
                    <PromoBadges promocoes={item.promocoes} testIdPrefix={`listing-${item.id}`} />
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="text-base font-extrabold text-slate-900" data-testid={`marketplace-listing-title-${item.id}`}>
                        <Link to={listingUrl(item)} className="hover:text-emerald-600 hover:underline" data-testid={`marketplace-listing-link-${item.id}`}>{item.titulo}</Link>
                      </h4>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${item.tipo === 'motorista' ? 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200' : item.tipo === 'venda' ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'}`} data-testid={`marketplace-listing-type-${item.id}`}>
                        {item.tipo === 'viatura' ? t.tabCars : item.tipo === 'venda' ? 'Venda' : t.tabDrivers}
                      </span>
                    </div>

                    <p className="inline-flex items-center gap-1.5 text-sm text-slate-500" data-testid={`marketplace-listing-city-${item.id}`}>
                      <MapPin className="h-4 w-4 text-emerald-500" /> {item.cidade}
                    </p>

                    {item.contacto?.nome_publico && (
                      <p className="text-xs text-slate-500" data-testid={`marketplace-listing-public-name-${item.id}`}>
                        {item.tipo === 'motorista' ? 'Publicado por' : 'Anunciado por'} <strong className="font-semibold text-slate-700">{item.contacto.nome_publico}</strong>
                      </p>
                    )}

                    {item.tipo !== 'motorista' ? (
                      <>
                        <div className="flex flex-wrap gap-1.5 text-xs">
                          {item.detalhes?.combustivel && (
                            <span className="rounded-md bg-slate-100 px-2 py-1 text-slate-700">
                              {item.detalhes.combustivel}
                            </span>
                          )}
                          {item.detalhes?.caixa && (
                            <span className="rounded-md bg-slate-100 px-2 py-1 text-slate-700">
                              {item.detalhes.caixa}
                            </span>
                          )}
                          {item.detalhes?.marca && (
                            <span className="rounded-md bg-slate-100 px-2 py-1 text-slate-700" data-testid={`marketplace-listing-brand-${item.id}`}>
                              {item.detalhes.marca} {item.detalhes?.modelo}
                            </span>
                          )}
                        </div>

                        <div className="flex items-baseline gap-2 pt-1" data-testid={`marketplace-listing-price-${item.id}`}>
                          {item.tipo === 'venda' ? (
                            <>
                              <span className="text-2xl font-extrabold text-amber-600">€{item.detalhes?.preco_venda}</span>
                              <span className="text-sm text-slate-500">à venda{item.detalhes?.quilometros ? ` · ${Number(item.detalhes.quilometros).toLocaleString('pt-PT')} km` : ''}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-2xl font-extrabold text-emerald-600">€{item.detalhes?.valor_semanal}</span>
                              <span className="text-sm text-slate-500">/ semana</span>
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="grid gap-1.5 text-sm text-slate-700">
                        <p data-testid={`marketplace-listing-driver-interest-${item.id}`}>
                          <span className="text-slate-400">Interesse:</span> {item.detalhes?.interesse}
                        </p>
                        <p data-testid={`marketplace-listing-driver-type-${item.id}`}>
                          <span className="text-slate-400">Tipo viatura:</span> {item.detalhes?.tipo_viatura}
                        </p>
                        <p data-testid={`marketplace-listing-driver-fuel-${item.id}`}>
                          <span className="text-slate-400">Combustível:</span> {item.detalhes?.combustivel}
                        </p>
                        {item.detalhes?.valor_aluguer_maximo && (
                          <p className="font-semibold text-emerald-600" data-testid={`marketplace-listing-driver-budget-${item.id}`}>
                            Valor aluguer máximo: €{item.detalhes.valor_aluguer_maximo}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs text-slate-500" data-testid={`marketplace-listing-contact-box-${item.id}`}>
                      <p className="mb-1.5 flex items-center gap-2 font-semibold text-slate-700">
                        <Mail className="h-4 w-4 text-emerald-500" /> {item.contacto?.email}
                      </p>
                      {item.contacto?.telefone ? (
                        <p className="flex items-center gap-2" data-testid={`marketplace-listing-contact-visible-${item.id}`}>
                          <Phone className="h-4 w-4 text-emerald-500" /> {item.contacto.telefone}
                        </p>
                      ) : (
                        <p data-testid={`marketplace-listing-contact-hidden-${item.id}`}>Contacto privado (usar mensagem).</p>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Link
                        to={listingUrl(item)}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-700"
                        data-testid={`marketplace-listing-view-button-${item.id}`}
                      >
                        Ver anúncio
                      </Link>
                      <button
                        onClick={() => {
                          setSelectedListing(item);
                          setLeadState({ loading: false, msg: '', err: '' });
                        }}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-white px-4 py-2.5 text-sm font-bold text-emerald-700 transition hover:bg-emerald-50"
                        data-testid={`marketplace-listing-contact-button-${item.id}`}
                      >
                        <MessageSquare className="h-4 w-4" /> {t.contact}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        </div>
      </main>

      {selectedListing && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/35 p-4" data-testid="marketplace-lead-modal">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="mb-3 flex items-start justify-between">
              <h4 className="text-lg font-bold" data-testid="marketplace-lead-modal-title">{t.leadTitle}</h4>
              <button onClick={() => setSelectedListing(null)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs" data-testid="marketplace-lead-modal-close">Fechar</button>
            </div>

            <p className="mb-3 text-sm text-slate-600" data-testid="marketplace-lead-modal-listing">{selectedListing.titulo}</p>

            <form onSubmit={sendLead} className="grid gap-3">
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Nome" value={leadForm.nome} onChange={(e) => setLeadForm((prev) => ({ ...prev, nome: e.target.value }))} data-testid="marketplace-lead-name" required />
              <input className="rounded-xl border border-slate-300 px-3 py-2" type="email" placeholder="Email" value={leadForm.email} onChange={(e) => setLeadForm((prev) => ({ ...prev, email: e.target.value }))} data-testid="marketplace-lead-email" required />
              <input className="rounded-xl border border-slate-300 px-3 py-2" placeholder="Telefone (opcional)" value={leadForm.telefone} onChange={(e) => setLeadForm((prev) => ({ ...prev, telefone: e.target.value }))} data-testid="marketplace-lead-phone" />
              <p className="-mt-1 text-xs text-slate-500" data-testid="marketplace-lead-phone-note">Só precisa de indicar o telemóvel se quiser partilhá-lo com o anunciante (exige verificação por SMS). Caso contrário, será contactado por email.</p>

              <div className="space-y-2 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/50 p-3">
                <p className="text-xs font-semibold text-slate-600">1) Verifique o seu email</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => sendLeadEmailCode(leadForm.email)} disabled={leadEmailState.sending} className="rounded-lg border border-emerald-300 px-3 py-2 text-xs font-semibold text-emerald-700 disabled:opacity-60" data-testid="lead-send-email-code">{leadEmailState.sending ? 'A enviar...' : (leadEmailState.sent ? '✓ Reenviar' : 'Enviar código')}</button>
                  <input className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Código email" value={leadForm.code} onChange={(e) => setLeadForm((prev) => ({ ...prev, code: e.target.value }))} data-testid="lead-email-code" />
                  <button type="button" onClick={verifyLeadEmailCode} disabled={leadEmailState.verifying || leadEmailState.verified} className={`rounded-lg border px-3 py-2 text-xs font-semibold disabled:opacity-60 ${leadEmailState.verified ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-emerald-300 text-emerald-700'}`} data-testid="lead-verify-email">{leadEmailState.verified ? '✓' : 'Validar'}</button>
                </div>
                {leadEmailState.err && <div className="text-xs text-red-600" data-testid="lead-email-error">{leadEmailState.err}</div>}
              </div>

              <div className="space-y-2 rounded-xl border border-dashed border-cyan-300 bg-cyan-50/50 p-3" data-testid="lead-sms-block" style={{ display: leadForm.telefone.trim() ? 'block' : 'none' }}>
                <p className="text-xs font-semibold text-slate-600">2) Verifique o seu telemóvel (SMS) — opcional</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => sendPhoneCode(leadForm.telefone, leadPhoneState, setLeadPhoneState)} disabled={leadPhoneState.sending} className="rounded-lg border border-cyan-300 px-3 py-2 text-xs font-semibold text-cyan-700 disabled:opacity-60" data-testid="lead-send-sms-code">{leadPhoneState.sending ? 'A enviar...' : (leadPhoneState.sent ? '✓ Reenviar' : 'Enviar SMS')}</button>
                  <input className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Código SMS" value={leadForm.phone_code} onChange={(e) => setLeadForm((prev) => ({ ...prev, phone_code: e.target.value }))} data-testid="lead-sms-code" />
                  <button type="button" onClick={() => verifyPhoneCode(leadForm.telefone, leadForm.phone_code, setLeadForm, 'phone_verification_token', setLeadPhoneState)} disabled={leadPhoneState.verifying || leadPhoneState.verified} className={`rounded-lg border px-3 py-2 text-xs font-semibold disabled:opacity-60 ${leadPhoneState.verified ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-cyan-300 text-cyan-700'}`} data-testid="lead-verify-sms">{leadPhoneState.verified ? '✓' : 'Validar'}</button>
                </div>
                {leadPhoneState.err && <div className="text-xs text-red-600" data-testid="lead-sms-error">{leadPhoneState.err}</div>}
              </div>

              <textarea className="rounded-xl border border-slate-300 px-3 py-2" rows={3} placeholder="Mensagem" value={leadForm.mensagem} onChange={(e) => setLeadForm((prev) => ({ ...prev, mensagem: e.target.value }))} data-testid="marketplace-lead-message" required />

              <label className="flex items-start gap-2 text-xs text-slate-600" data-testid="lead-privacy-consent">
                <input type="checkbox" className="mt-0.5" checked={leadForm.aceita_privacidade} onChange={(e) => setLeadForm((prev) => ({ ...prev, aceita_privacidade: e.target.checked }))} data-testid="lead-privacy-checkbox" />
                <span>Li a <Link to="/privacidade" target="_blank" className="font-semibold text-emerald-600 underline">Política de Privacidade</Link> e aceito que os meus dados e a minha mensagem sejam enviados ao anunciante para resposta a este contacto.</span>
              </label>
              <label className="flex items-start gap-2 text-xs text-slate-600" data-testid="lead-alerts-consent">
                <input type="checkbox" className="mt-0.5" checked={leadForm.aceita_alertas} onChange={(e) => setLeadForm((prev) => ({ ...prev, aceita_alertas: e.target.checked }))} data-testid="lead-alerts-checkbox" />
                <span>Quero receber alertas de novas viaturas e oportunidades TVDE por email (opcional).</span>
              </label>

              <button type="submit" className="rounded-xl bg-emerald-500 px-4 py-2 font-semibold text-white hover:bg-emerald-600 disabled:opacity-60" disabled={leadState.loading} data-testid="marketplace-lead-submit">
                {leadState.loading ? 'A enviar...' : t.leadSubmit}
              </button>
            </form>

            {leadState.msg && <p className="mt-3 text-sm font-semibold text-emerald-700" data-testid="marketplace-lead-success">{leadState.msg}</p>}
            {leadState.err && <p className="mt-3 text-sm font-semibold text-red-600" data-testid="marketplace-lead-error">{leadState.err}</p>}
          </div>
        </div>
      )}

      {/* Modal de sucesso de publicação — mostra a data de expiração */}
      {publishSuccess && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/45 p-4" data-testid="marketplace-publish-success-modal">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200">
                <BadgeCheck className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-slate-900" data-testid="publish-success-title">
                  Anúncio submetido com sucesso
                </h4>
                <p className="mt-1 text-sm text-slate-600">
                  {publishSuccess.tipo === 'viatura'
                    ? 'A sua viatura foi recebida e está pendente de validação por um administrador. Será notificado por email após aprovação.'
                    : 'O seu perfil foi recebido e está pendente de validação por um administrador.'}
                </p>
              </div>
            </div>

            {publishSuccess.expires_at && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-slate-800" data-testid="publish-success-expiry">
                <p className="font-semibold text-emerald-800">
                  📅 O seu anúncio estará ativo até{' '}
                  <span className="font-extrabold">
                    {new Date(publishSuccess.expires_at).toLocaleDateString('pt-PT', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                </p>
                <p className="mt-1 text-xs text-slate-600">
                  Duração escolhida: <strong>{publishSuccess.duracao_dias} dias</strong>. Após esta data o anúncio será automaticamente removido. Receberá um email a perguntar se quer prolongar.
                </p>
              </div>
            )}

            <button
              onClick={() => { setPublishSuccess(null); if (routeMode) navigate('/'); }}
              className="mt-5 w-full rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600"
              data-testid="publish-success-close"
            >
              Entendi
            </button>
          </div>
        </div>
      )}

      {/* Modal de confirmação antes de submeter — quando contacto fica visível */}
      {confirmSubmit && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/45 p-4" data-testid="marketplace-confirm-submit-modal">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="mb-3 flex items-start gap-3">
              <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-1 ring-amber-200">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-slate-900" data-testid="confirm-submit-title">
                  Confirmar partilha de contacto
                </h4>
                <p className="mt-1 text-sm text-slate-600">
                  Vai partilhar publicamente o seu <strong>telefone</strong> e <strong>email</strong> no anúncio. Qualquer visitante poderá vê-los. Tem a certeza?
                </p>
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-emerald-500" />
                <span className="font-semibold">Telefone:</span>{' '}
                {confirmSubmit.type === 'viatura' ? carForm.telefone : driverForm.telefone}
              </p>
              <p className="mt-1.5 flex items-center gap-2">
                <Mail className="h-4 w-4 text-emerald-500" />
                <span className="font-semibold">Email:</span>{' '}
                {confirmSubmit.type === 'viatura' ? carForm.email : driverForm.email}
              </p>
            </div>

            <p className="mt-3 text-xs text-slate-500">
              💡 Pode preferir esconder o telefone — assim só receberá contactos pela mensagem do marketplace (o email continua visível para resposta).
            </p>

            <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                onClick={() => setConfirmSubmit(null)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                data-testid="confirm-submit-edit"
              >
                Alterar
              </button>
              <button
                onClick={() => {
                  const type = confirmSubmit.type;
                  setConfirmSubmit(null);
                  if (type === 'viatura') doSubmitCar();
                  else doSubmitDriver();
                }}
                className="rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600"
                data-testid="confirm-submit-confirm"
              >
                Confirmar e publicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal "Gerir os meus anúncios" — fluxo email → código → lista → apagar */}
      {manageState.open && (
        <div className="fixed inset-0 z-[95] flex items-stretch justify-center bg-black/45 p-0 sm:items-center sm:p-4" data-testid="marketplace-manage-modal">
          <div className="flex h-full w-full max-w-2xl flex-col overflow-y-auto border-slate-200 bg-white p-4 shadow-2xl sm:h-auto sm:max-h-[92vh] sm:rounded-2xl sm:border sm:p-6">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h4 className="text-lg font-bold text-slate-900" data-testid="manage-modal-title">
                  Gerir os meus anúncios
                </h4>
                <p className="mt-0.5 text-xs text-slate-500">
                  Confirme o email usado para publicar para gerir validade, extras, estado e dados do anúncio.
                </p>
              </div>
              <button
                onClick={closeManageModal}
                className="rounded-lg border border-slate-300 px-2 py-1 text-xs"
                data-testid="manage-modal-close"
              >
                Fechar
              </button>
            </div>

            {manageState.step === 'email' && (
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-slate-700">Email da publicação</label>
                <input
                  type="email"
                  value={manageState.email}
                  onChange={(e) => setManageState((s) => ({ ...s, email: e.target.value }))}
                  placeholder="o.seu@email.com"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-800"
                  data-testid="manage-email-input"
                />
                <button
                  onClick={manageSendCode}
                  disabled={manageState.loading}
                  className="w-full rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600 disabled:opacity-60"
                  data-testid="manage-send-code"
                >
                  {manageState.loading ? 'A enviar...' : 'Enviar código por email'}
                </button>
              </div>
            )}

            {manageState.step === 'code' && (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Enviámos um código de 6 dígitos para <strong>{manageState.email}</strong>.
                </p>
                <input
                  value={manageState.code}
                  onChange={(e) => setManageState((s) => ({ ...s, code: e.target.value }))}
                  placeholder="Código de 6 dígitos"
                  inputMode="numeric"
                  maxLength={6}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-center text-lg tracking-widest text-slate-800"
                  data-testid="manage-code-input"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setManageState((s) => ({ ...s, step: 'email', code: '', err: '' }))}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    data-testid="manage-back-email"
                  >
                    Voltar
                  </button>
                  <button
                    onClick={manageConfirmCode}
                    disabled={manageState.loading}
                    className="flex-1 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600 disabled:opacity-60"
                    data-testid="manage-confirm-code"
                  >
                    {manageState.loading ? 'A validar...' : 'Validar código'}
                  </button>
                </div>
              </div>
            )}

            {manageState.step === 'list' && (
              <div className="space-y-3" data-testid="manage-listing-list">
                {manageState.items.length === 0 ? (
                  <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-600">
                    Não foram encontrados anúncios com o email <strong>{manageState.email}</strong>.
                  </p>
                ) : (
                  manageState.items.map((it) => (
                    <div
                      key={it.id}
                      className="rounded-xl border border-slate-200 bg-white p-3"
                      data-testid={`manage-listing-item-${it.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-slate-900">{it.titulo}</p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {it.tipo === 'viatura' ? 'Viatura (aluguer)' : it.tipo === 'venda' ? 'Viatura à venda' : 'Motorista'} · {it.cidade}
                          {it.status && !commerce[it.id] && (
                            <span className="ml-2 inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 ring-1 ring-emerald-200">
                              {it.status}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                      <button
                        onClick={() => (editDraft && editDraft.id === it.id ? setEditDraft(null) : startEditListing(it))}
                        disabled={manageState.loading}
                        className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-60"
                        data-testid={`manage-edit-${it.id}`}
                      >
                        {editDraft && editDraft.id === it.id ? 'Fechar' : 'Editar'}
                      </button>
                      </div>
                      </div>

                      <ListingCommerceBlock
                        data={commerce[it.id]}
                        busy={manageState.loading}
                        onAction={commerceAction}
                        onOrder={commerceOrder}
                      />

                      {commerce[it.id] && (
                        <PublicNameCard
                          listingId={it.id}
                          identidade={commerce[it.id].identidade}
                          declarado={commerce[it.id].contacto_declarado}
                          email={manageState.email.trim()}
                          token={manageState.token}
                          onSaved={(identidade) => setCommerce((prev) => ({
                            ...prev, [it.id]: { ...prev[it.id], identidade },
                          }))}
                        />
                      )}

                      <div className="mt-3 flex justify-end border-t border-dashed border-red-200 pt-3">
                        <button
                          onClick={() => manageDeleteListing(it.id)}
                          disabled={manageState.loading}
                          className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 transition hover:bg-red-100 disabled:opacity-60"
                          data-testid={`manage-delete-${it.id}`}
                        >
                          Apagar anúncio definitivamente
                        </button>
                      </div>

                      {editDraft && editDraft.id === it.id && (
                        <div className="mt-3 grid grid-cols-1 gap-2 border-t border-slate-100 pt-3 sm:grid-cols-2" data-testid={`manage-edit-form-${it.id}`}>
                          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm sm:col-span-2" placeholder="Título" value={editDraft.titulo} onChange={(e) => setEditDraft((d) => ({ ...d, titulo: e.target.value }))} data-testid="manage-edit-titulo" />
                          <textarea className="rounded-lg border border-slate-300 px-3 py-2 text-sm sm:col-span-2" rows={2} placeholder="Descrição" value={editDraft.descricao} onChange={(e) => setEditDraft((d) => ({ ...d, descricao: e.target.value }))} data-testid="manage-edit-descricao" />
                          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Cidade" value={editDraft.cidade} onChange={(e) => setEditDraft((d) => ({ ...d, cidade: e.target.value }))} data-testid="manage-edit-cidade" />
                          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Telefone" value={editDraft.telefone} onChange={(e) => setEditDraft((d) => ({ ...d, telefone: e.target.value }))} data-testid="manage-edit-telefone" />
                          {editDraft.tipo === 'viatura' && (
                            <>
                              <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Marca" value={editDraft.marca} onChange={(e) => setEditDraft((d) => ({ ...d, marca: e.target.value }))} data-testid="manage-edit-marca" />
                              <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Modelo" value={editDraft.modelo} onChange={(e) => setEditDraft((d) => ({ ...d, modelo: e.target.value }))} data-testid="manage-edit-modelo" />
                            </>
                          )}
                          <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Combustível" value={editDraft.combustivel} onChange={(e) => setEditDraft((d) => ({ ...d, combustivel: e.target.value }))} data-testid="manage-edit-combustivel" />
                          {editDraft.tipo === 'venda' && (
                            <>
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                Preço de venda (€)
                                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" type="number" value={editDraft.preco} onChange={(e) => setEditDraft((d) => ({ ...d, preco: e.target.value }))} data-testid="manage-edit-preco" />
                              </label>
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                Quilómetros
                                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" type="number" value={editDraft.quilometros} onChange={(e) => setEditDraft((d) => ({ ...d, quilometros: e.target.value }))} data-testid="manage-edit-km" />
                              </label>
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                Estado da viatura
                                <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={editDraft.estado_principal} onChange={(e) => setEditDraft((d) => ({ ...d, estado_principal: e.target.value }))} data-testid="manage-edit-estado-viatura">
                                  <option value="operacional">Operacional</option>
                                  <option value="operacional_anomalias">Operacional com anomalias</option>
                                  <option value="avariada">Avariada</option>
                                  <option value="imobilizada">Imobilizada</option>
                                  <option value="pecas">Para peças</option>
                                </select>
                              </label>
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
                                Categoria
                                <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={editDraft.categoria} onChange={(e) => setEditDraft((d) => ({ ...d, categoria: e.target.value }))} data-testid="manage-edit-categoria">
                                  <option value="ex_tvde">Ex-TVDE</option>
                                  <option value="oportunidade">Oportunidade para TVDE</option>
                                </select>
                              </label>
                              <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                                <input type="checkbox" checked={editDraft.preco_negociavel} onChange={(e) => setEditDraft((d) => ({ ...d, preco_negociavel: e.target.checked }))} data-testid="manage-edit-negociavel" />
                                Preço negociável
                              </label>
                              <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                                <input type="checkbox" checked={editDraft.venda_urgente} onChange={(e) => setEditDraft((d) => ({ ...d, venda_urgente: e.target.checked }))} data-testid="manage-edit-urgente" />
                                Venda urgente
                              </label>
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600 sm:col-span-2">
                                Equipamentos e extras
                                <textarea className="rounded-lg border border-slate-300 px-3 py-2 text-sm" rows={2} value={editDraft.equipamentos} onChange={(e) => setEditDraft((d) => ({ ...d, equipamentos: e.target.value }))} data-testid="manage-edit-equipamentos" />
                              </label>
                              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600 sm:col-span-2">
                                Defeitos ou aspetos a conhecer
                                <textarea className="rounded-lg border border-slate-300 px-3 py-2 text-sm" rows={2} value={editDraft.defeitos} onChange={(e) => setEditDraft((d) => ({ ...d, defeitos: e.target.value }))} data-testid="manage-edit-defeitos" />
                              </label>
                            </>
                          )}
                          {editDraft.tipo !== 'venda' && (
                            <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" type="number" placeholder={editDraft.tipo === 'viatura' ? 'Valor/semana (€)' : 'Valor máximo aluguer (€)'} value={editDraft.valor} onChange={(e) => setEditDraft((d) => ({ ...d, valor: e.target.value }))} data-testid="manage-edit-valor" />
                          )}
                          <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                            <input type="checkbox" checked={editDraft.mostrar_telefone} onChange={(e) => setEditDraft((d) => ({ ...d, mostrar_telefone: e.target.checked }))} data-testid="manage-edit-mostrar-telefone" />
                            Mostrar telefone
                          </label>
                          <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                            <input type="checkbox" checked={editDraft.mostrar_email} onChange={(e) => setEditDraft((d) => ({ ...d, mostrar_email: e.target.checked }))} data-testid="manage-edit-mostrar-email" />
                            Mostrar email
                          </label>
                          <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`manage-edit-fotos-${it.id}`}>
                            <div className="mb-2 flex items-center justify-between">
                              <span className="text-xs font-bold text-slate-700">Fotografias ({(editDraft.fotos || []).length}/6)</span>
                            </div>
                            {(editDraft.fotos || []).length > 0 && (
                              <div className="mb-2 grid grid-cols-3 gap-2 sm:grid-cols-6">
                                {(editDraft.fotos || []).map((src, i) => (
                                  <div key={i} className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200">
                                    <img src={resolveImageUrl(src)} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" />
                                    <button
                                      type="button"
                                      onClick={() => removeEditPhoto(i)}
                                      className="absolute right-1 top-1 rounded-full bg-red-600 p-1 text-white opacity-90 transition hover:bg-red-700"
                                      aria-label="Remover foto"
                                      data-testid={`manage-edit-remove-foto-${i}`}
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {(editDraft.fotos || []).length < 6 && (
                              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100">
                                <ImagePlus className="h-4 w-4" />
                                {editDraft.fotosUploading ? 'A carregar...' : 'Adicionar fotos'}
                                <input type="file" accept="image/*" multiple className="hidden" onChange={onEditPhotos} disabled={editDraft.fotosUploading} data-testid="manage-edit-add-fotos" />
                              </label>
                            )}
                          </div>
                          <button
                            onClick={manageSaveEdit}
                            disabled={manageState.loading || editDraft.fotosUploading}
                            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60 sm:col-span-2"
                            data-testid={`manage-save-${it.id}`}
                          >
                            {manageState.loading ? 'A guardar...' : 'Guardar alterações'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {manageState.msg && (
              <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800" data-testid="manage-success">
                {manageState.msg}
              </p>
            )}
            {manageState.err && (
              <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="manage-error">
                {manageState.err}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Lightbox público — abre quando o utilizador clica na foto de um anúncio */}
      {lightbox.open && lightbox.fotos.length > 0 && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-3"
          onClick={closeLightbox}
          data-testid="marketplace-lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-slate-900 shadow hover:bg-white"
            data-testid="marketplace-lightbox-close"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>

          <div
            className="relative flex max-h-[92vh] w-full max-w-5xl flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={resolveImageUrl(lightbox.fotos[lightbox.idx])}
              alt={`${lightbox.title || 'Foto'} ${lightbox.idx + 1} de ${lightbox.fotos.length}`}
              className="max-h-[80vh] w-auto rounded-xl object-contain"
              data-testid="marketplace-lightbox-photo"
            />

            {lightbox.fotos.length > 1 && (
              <>
                <button
                  onClick={lightboxPrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-slate-900 shadow hover:bg-white"
                  data-testid="marketplace-lightbox-prev"
                  aria-label="Foto anterior"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={lightboxNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-slate-900 shadow hover:bg-white"
                  data-testid="marketplace-lightbox-next"
                  aria-label="Próxima foto"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <div className="mt-3 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-white">
                  {lightbox.idx + 1} / {lightbox.fotos.length}
                </div>
                <div className="mt-3 flex gap-2 overflow-x-auto px-2 pb-2" data-testid="marketplace-lightbox-thumbs">
                  {lightbox.fotos.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => setLightbox((s) => ({ ...s, idx: i }))}
                      className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg border-2 ${i === lightbox.idx ? 'border-emerald-500' : 'border-transparent'}`}
                    >
                      <img src={resolveImageUrl(src)} alt={`Miniatura ${i + 1}`} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {checkoutSummary && (
        <div className="fixed inset-0 z-[92] flex items-center justify-center bg-black/50 p-4" data-testid="checkout-summary-modal">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-extrabold text-slate-900">Resumo do anúncio</h3>
            <p className="mt-1 text-xs text-slate-500">Confirme os detalhes antes de pagar.</p>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Produto</dt><dd className="font-semibold text-slate-900" data-testid="checkout-produto">{checkoutSummary.pacote?.label || 'Anúncio Marketplace'}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Duração</dt><dd className="font-semibold text-slate-900">{checkoutSummary.duracao_dias || checkoutSummary.pacote?.days || 7} dias</dd></div>
              {mpSettings?.comercio_ativo && (
                <div className="flex justify-between"><dt className="text-slate-500">Meios de pagamento</dt><dd className="font-semibold text-slate-900">Cartão · MB WAY · Multibanco</dd></div>
              )}
              <div className="flex justify-between"><dt className="text-slate-500">Início previsto</dt><dd className="font-semibold text-slate-900">Após pagamento e revisão</dd></div>
              <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-base"><dt className="font-bold text-slate-900">Total (IVA incl.)</dt><dd className="font-extrabold text-emerald-600" data-testid="checkout-total">{Number(checkoutSummary.pacote?.price || 0).toFixed(2)} €</dd></div>
            </dl>
            <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
              Renovação: o anúncio expira ao fim do período e pode ser renovado. Cancelamento: se o anúncio for
              rejeitado na revisão, o valor é integralmente reembolsado. Ver{' '}
              <Link to="/termos" target="_blank" className="text-emerald-600 underline">Termos</Link> e{' '}
              <Link to="/livre-resolucao" target="_blank" className="text-emerald-600 underline">livre resolução</Link>.
            </p>
            <label className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="checkout-consent-label">
              <input type="checkbox" className="mt-0.5" checked={checkoutConsent} onChange={(e) => setCheckoutConsent(e.target.checked)} data-testid="checkout-consent-checkbox" />
              <span>Solicito expressamente que a publicação do anúncio se inicie antes do fim do prazo legal de 14 dias. Reconheço que, após a prestação integral do serviço, poderei perder o direito de livre resolução nos termos legalmente aplicáveis.</span>
            </label>
            {checkoutError && <p className="mt-2 text-xs font-semibold text-red-600" data-testid="checkout-error">{checkoutError}</p>}
            <div className="mt-4 flex gap-2">
              <button onClick={() => setCheckoutSummary(null)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700" data-testid="checkout-cancel">Cancelar</button>
              <button onClick={proceedCheckout} disabled={checkoutLoading} className="flex-1 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:opacity-60" data-testid="checkout-pay-btn">
                {checkoutLoading ? 'A abrir pagamento...' : `Pagar ${Number(checkoutSummary.pacote?.price || 0).toFixed(2)} € e submeter anúncio`}
              </button>
            </div>
          </div>
        </div>
      )}

      <NewsHomeBlock />
      <FaqSection />
      <MarketplaceFooter onManageClick={openManageModal} />
    </div>
  );
}
